import {
  pgTable,
  text,
  varchar,
  timestamp,
  jsonb,
  index,
  uniqueIndex,
  serial,
  integer,
  boolean,
  uuid,
  bigint,
  decimal,
  date,
  check,
  type AnyPgColumn,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { relations, sql } from "drizzle-orm";
import {
  xssSafeString,
  xssSafeEmail,
  xssSafeUrl,
  xssSafeTextarea,
  xssSafeOrganizationName,
  xssSafeUserName,
  xssSafePhone,
  xssSafeFilename,
} from "./validation";
import {
  RISK_STATUSES,
  RISK_CATEGORIES,
  RISK_SEVERITIES,
  RISK_EVENT_TYPES,
  RISK_SOURCE_TYPES,
} from "./riskLifecycle";

// Industry enum for customers
export const CUSTOMER_INDUSTRIES = [
  "healthcare",
  "financial_services",
  "education",
  "legal",
  "manufacturing",
  "professional_services",
  "technology",
  "retail",
  "government",
  "nonprofit",
  "other"
] as const;

export type CustomerIndustry = typeof CUSTOMER_INDUSTRIES[number];

// Shape of the organizations.settings jsonb column. Fields are optional because
// existing rows may predate newer settings; an index signature allows forward-compatible
// additions without breaking the type.
export interface OrganizationSettings {
  brandName?: string | null;
  logo?: string | null;
  primaryColor?: string | null;
  defaultRole?: string | null;
  features?: {
    customReports?: boolean;
    apiAccess?: boolean;
    sso?: boolean;
    webhooks?: boolean;
    [key: string]: unknown;
  };
  sso?: {
    clientId?: string | null;
    clientSecret?: string | null;
    tenantId?: string | null;
    [key: string]: unknown;
  };
  teams?: {
    enabled?: boolean;
    clientId?: string | null;
    clientSecret?: string | null;
    tenantId?: string | null;
    [key: string]: unknown;
  };
  checklist?: {
    dismissed?: boolean;
    dismissedAt?: string | null;
    dismissedBy?: string | null;
    [key: string]: unknown;
  };
  onboarding?: Record<string, unknown>;
  security?: {
    maxConcurrentSessions?: number;
    enforceConcurrentSessionLimit?: boolean;
    sessionTimeout?: number; // Org-wide inactivity timeout in minutes (0 = never)
    [key: string]: unknown;
  };
  presentationTemplate?: {
    slides?: Record<string, boolean>;
    slideOrder?: string[];
    [key: string]: unknown;
  };
  // Whitelabel email sending domain. Lets an org send portal emails (customer
  // and internal categories) from their own verified domain. Security emails
  // always stay on the TechAlign domain regardless of this setting. Stored in
  // the settings jsonb column (additive — no DDL required).
  emailSender?: {
    domain?: string | null;
    fromEmail?: string | null;
    fromName?: string | null;
    replyTo?: string | null;
    provider?: string | null; // which provider registered the domain, e.g. "sendgrid"
    providerDomainId?: string | null; // provider-side domain authentication id
    dnsRecords?: Array<{
      type: string;
      host: string;
      value: string;
      purpose: string;
      valid?: boolean;
    }>;
    verificationStatus?: 'pending' | 'verified' | 'failed' | null;
    verifiedAt?: string | null;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// Allowed org-wide inactivity session timeout values, in minutes (0 = never).
// Shared between the org settings UI, profile display, and server validation.
export const ALLOWED_SESSION_TIMEOUTS = [15, 30, 60, 120, 480, 0] as const;
// Default org-wide inactivity timeout (minutes) used when settings.security.sessionTimeout is unset.
export const DEFAULT_SESSION_TIMEOUT = 60;

// Organizations table - each organization is completely isolated
export const organizations = pgTable("organizations", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name").notNull().unique(), // Organization name must be globally unique
  slug: varchar("slug").notNull().unique(), // Auto-generated from name for URLs
  subscriptionTier: varchar("subscription_tier").notNull().default("trial"), // trial, starter, professional, enterprise
  subscriptionStatus: varchar("subscription_status").notNull().default("trial"), // trial, active, suspended, cancelled
  subscriptionExpiresAt: timestamp("subscription_expires_at"),
  trialStartedAt: timestamp("trial_started_at"),
  paddleSubscriptionId: varchar("paddle_subscription_id"), // Paddle subscription ID for billing (legacy)
  paddleCustomerId: varchar("paddle_customer_id"), // Paddle customer ID (legacy)
  stripeSubscriptionId: varchar("stripe_subscription_id"), // Stripe subscription ID for billing
  stripeCustomerId: varchar("stripe_customer_id"), // Stripe customer ID
  billingInterval: varchar("billing_interval").default("monthly"), // monthly, yearly
  lastPlanChangeAt: timestamp("last_plan_change_at"), // Timestamp of last plan change for frequency limiting
  // Pending subscription changes for downgrades/upgrades that take effect later
  pendingChangeType: varchar("pending_change_type"), // "upgrade", "downgrade", "billing_change", "cancellation"
  pendingTier: varchar("pending_tier"), // The tier the subscription will change to
  pendingBillingInterval: varchar("pending_billing_interval"), // New billing interval if changing
  pendingEffectiveDate: timestamp("pending_effective_date"), // When the change takes effect
  pendingPriceId: varchar("pending_price_id"), // Paddle price ID for the pending change
  settings: jsonb("settings").$type<OrganizationSettings>().default({
    brandName: null,
    logo: null,
    primaryColor: "#3b82f6",
    defaultRole: "alignment_engineer",
    features: {
      customReports: false,
      apiAccess: false,
      sso: false,
      webhooks: false,
    },
    sso: {
      // Organization-specific SSO configuration
      // clientId, clientSecret, tenantId, etc. will be stored here
    },
    teams: {
      // Organization-specific Microsoft Teams integration
      // clientId, clientSecret, tenantId for Teams API access
      enabled: false,
      clientId: null,
      clientSecret: null,
      tenantId: null,
    },
    checklist: {
      // First-time setup checklist state
      dismissed: false,
      dismissedAt: null,
      dismissedBy: null, // User ID who dismissed it
    },
    security: {
      // Security settings for the organization
      maxConcurrentSessions: 5, // Maximum concurrent sessions per user (default: 5)
      enforceConcurrentSessionLimit: true, // Whether to enforce the limit
      sessionTimeout: 60, // Org-wide inactivity timeout in minutes (0 = never)
    },
    presentationTemplate: {
      // Default presentation template for Business Reviews
      slides: {
        title: true,
        executiveSummary: true,
        keyFindings: true,
        recommendations: true,
        budgetPlanning: true,
        scheduleNextReview: true,
      },
      // Order of slides (array of slide type keys)
      slideOrder: ["title", "executiveSummary", "keyFindings", "recommendations", "budgetPlanning", "scheduleNextReview"],
    },
  }),
  ownerUserId: varchar("owner_user_id"), // References users.id - the user who created this org
  maxCustomers: integer("max_customers").default(20), // Starter plan default
  maxAssessments: integer("max_assessments").default(250), // Starter plan default
  // Org-level default grandfathering / transition window in days (0–3,650, Zod-enforced).
  // Additive + nullable (standard-change-propagation spec, Req 6.2): null ⇒ no org default.
  defaultTransitionWindowDays: integer("default_transition_window_days"),
  encryptionKey: varchar("encryption_key"), // Organization-specific encryption key for sensitive data
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Session storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

// User storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const users = pgTable("users", {
  id: varchar("id").primaryKey().notNull(),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  passwordHash: varchar("password_hash"), // For local authentication
  role: varchar("role").notNull().default("alignment_engineer"), // Organization-specific role - Owner role is non-removable unless transferred
  authMethod: varchar("auth_method").default("local"), // local, entra
  organizationId: uuid("organization_id").references(() => organizations.id), // User belongs to one organization only
  isOwner: boolean("is_owner").default(false), // Whether this user owns their organization
  // Email verification
  emailVerified: boolean("email_verified").default(false),
  emailVerificationCode: varchar("email_verification_code"),
  emailVerificationExpires: timestamp("email_verification_expires"),
  // Security settings
  mfaEnabled: boolean("mfa_enabled").default(false),
  mfaRequired: boolean("mfa_required").default(false), // Whether MFA is mandatory for this user (e.g., owners)
  mfaSecret: jsonb("mfa_secret").$type<any>(), // TOTP secret - encrypted with org key (format: {encrypted, iv, tag})
  mfaBackupCodes: jsonb("mfa_backup_codes").$type<any>(), // Backup codes - encrypted with org key (format: {encrypted, iv, tag})
  passwordLastChanged: timestamp("password_last_changed"),
  loginNotifications: boolean("login_notifications").default(true),
  sessionTimeout: integer("session_timeout").default(60), // Minutes
  // Superadmin designation (only tyler@kiptonic.com)
  isSuperadmin: boolean("is_superadmin").default(false),
  // Login control - can be disabled by admin/owner
  isLoginDisabled: boolean("is_login_disabled").default(false),
  loginDisabledAt: timestamp("login_disabled_at"),
  loginDisabledBy: varchar("login_disabled_by"),
  loginDisabledReason: text("login_disabled_reason"),
  // Password reset
  passwordResetToken: varchar("password_reset_token"),
  passwordResetExpires: timestamp("password_reset_expires"),
  // User timezone preference (defaults to UTC)
  timezone: varchar("timezone").default("UTC"),
  // User theme preference (light, dark, slate)
  theme: varchar("theme").default("dark"),
  // Last login timestamp for user management display
  lastLoginAt: timestamp("last_login_at"),
  // Notification Preferences - dual toggle system for platform and email notifications
  notificationPreferences: jsonb("notification_preferences").default({
    // Assessment Notifications (alignment engineers, vCIOs, admins, owners)
    assessmentDeadlines: { platform: true, email: true },
    assessmentAssignments: { platform: true, email: true },
    assessmentSubmissions: { platform: true, email: true },
    assessmentApprovals: { platform: true, email: true },
    assessmentReviews: { platform: true, email: true },

    // Business Review Notifications (vCIOs, admins, owners)
    qbrGeneration: { platform: true, email: true },
    customerUpdates: { platform: true, email: true },
    templateUpdates: { platform: true, email: true },
    weeklyReports: { platform: true, email: true },

    // Management Notifications (admins, owners)
    userManagement: { platform: true, email: true },
    newUserAdded: { platform: true, email: true },

    // System Notifications (admins, owners)
    systemMaintenance: { platform: true, email: true },
    securityAlerts: { platform: true, email: true },
  }),
  // Dashboard customization preferences
  dashboardLayout: jsonb("dashboard_layout").default([]), // User's custom dashboard widget layout
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  profileUpdatedAt: timestamp("profile_updated_at"), // When profile info (name, timezone) was last changed
});

// User login history tracking for admin dashboard
export const userLoginHistory = pgTable(
  "user_login_history",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    organizationId: uuid("organization_id").references(() => organizations.id, {
      onDelete: "cascade",
    }),
    loginAt: timestamp("login_at").defaultNow().notNull(),
    ipAddress: varchar("ip_address"),
    userAgent: text("user_agent"),
    authMethod: varchar("auth_method"), // local, entra
    sessionId: varchar("session_id"),
    logoutAt: timestamp("logout_at"),
    lastSeen: timestamp("last_seen"), // Track last activity time for session management
    deviceInfo: jsonb("device_info"), // Store device fingerprint/metadata
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_login_history_user").on(table.userId),
    index("idx_login_history_organization").on(table.organizationId),
    index("idx_login_history_login_at").on(table.loginAt),
    index("idx_login_history_active_sessions").on(table.userId, table.logoutAt), // For concurrent session tracking
  ],
);


// Role permissions configuration table
export const rolePermissions = pgTable("role_permissions", {
  id: serial("id").primaryKey(),
  role: varchar("role").notNull().unique(),
  displayName: varchar("display_name").notNull(),
  description: text("description"),
  permissions: text("permissions").array().notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const customers = pgTable(
  "customers",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    name: varchar("name").notNull(),
    type: varchar("type").notNull(), // enterprise, mid-market, small-business
    industry: varchar("industry"), // healthcare, financial_services, education, legal, manufacturing, professional_services, technology, retail, government, nonprofit, other
    // Structured address fields for better data import/export
    addressStreet: text("address_street"),
    addressCity: varchar("address_city"),
    addressState: varchar("address_state"),
    addressCountry: varchar("address_country"),
    addressZipCode: varchar("address_zip_code"),
    // Business review settings
    quarterlyReviewEnabled: boolean("quarterly_review_enabled").default(true),
    reviewFrequency: varchar("review_frequency").default("quarterly"), // monthly, quarterly, biannual, annual
    nextReviewDue: timestamp("next_review_due"), // Next scheduled review
    alignmentGoalScore: decimal("alignment_goal_score", {
      precision: 5,
      scale: 2,
    }).default("85.0"), // Target alignment percentage
    currentAlignmentScore: decimal("current_alignment_score", {
      precision: 5,
      scale: 2,
    }).default("0.0"), // Current calculated score
    lastReviewDate: timestamp("last_review_date"), // Last completed quarterly review
    yearlyBudgets: jsonb("yearly_budgets"), // Budget by year: { "2025": 100000, "2026": 120000 }
    presentationTemplate: jsonb("presentation_template"), // Customer-specific presentation template: { slides: {...}, slideOrder: [...] }
    // Customer branding
    logoUrl: varchar("logo_url"), // Path to customer logo in object storage (e.g., /objects/uploads/uuid)
    // PSA sync status
    deletedFromPsa: boolean("deleted_from_psa").default(false), // True if customer was removed from PSA
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [index("idx_customers_organization").on(table.organizationId)],
);

// Customer contacts table - contacts associated with customers for Business Reviews
export const customerContacts = pgTable(
  "customer_contacts",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    // Contact information
    firstName: varchar("first_name", { length: 100 }).notNull(),
    lastName: varchar("last_name", { length: 100 }).notNull(),
    email: varchar("email", { length: 255 }),
    phone: varchar("phone", { length: 50 }),
    mobile: varchar("mobile", { length: 50 }),
    jobTitle: varchar("job_title", { length: 150 }),
    department: varchar("department", { length: 100 }),
    // Contact classification
    isPrimary: boolean("is_primary").default(false),
    isBilling: boolean("is_billing").default(false),
    isTechnical: boolean("is_technical").default(false),
    isDecisionMaker: boolean("is_decision_maker").default(false),
    // PSA integration
    psaContactId: varchar("psa_contact_id", { length: 100 }), // External ID from ConnectWise/Autotask
    psaProvider: varchar("psa_provider", { length: 50 }), // 'connectwise', 'autotask', etc.
    lastSyncedAt: timestamp("last_synced_at"),
    deletedFromPsa: boolean("deleted_from_psa").default(false), // True if contact was removed from PSA
    // Notes
    notes: text("notes"),
    // Timestamps
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_contacts_organization").on(table.organizationId),
    index("idx_contacts_customer").on(table.customerId),
    index("idx_contacts_email").on(table.email),
    index("idx_contacts_psa").on(table.psaProvider, table.psaContactId),
    index("idx_contacts_org_customer").on(table.organizationId, table.customerId),
  ],
);

export const assessmentTemplates = pgTable(
  "assessment_templates",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id").references(() => organizations.id, {
      onDelete: "cascade",
    }), // null for global templates
    name: varchar("name").notNull(),
    category: varchar("category").notNull(), // security, infrastructure, compliance, general
    description: text("description"),
    questions: jsonb("questions").$type<any[]>().notNull(), // Array of question objects
    isGlobal: boolean("is_global").default(false), // Global templates available to all organizations
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_assessment_templates_organization").on(table.organizationId),
    index("idx_assessment_templates_global").on(table.isGlobal),
  ],
);

export const assessments = pgTable(
  "assessments",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id)
      .notNull(),
    templateId: integer("template_id")
      .references(() => assessmentTemplates.id)
      .notNull(),
    assignedToId: varchar("assigned_to_id")
      .references(() => users.id)
      .notNull(),
    vcioId: varchar("vcio_id").references(() => users.id), // vCIO assignment (nullable for backward compatibility)
    createdById: varchar("created_by_id")
      .references(() => users.id)
      .notNull(),
    reviewedById: varchar("reviewed_by_id").references(() => users.id),
    title: varchar("title").notNull(),
    status: varchar("status").notNull().default("draft"), // draft, pending_review, approved, completed
    responses: jsonb("responses"), // Assessment responses
    vcioNotes: jsonb("vcio_notes"), // vCIO review notes for each question
    qbrItems: jsonb("qbr_items"), // Items selected for QBR report
    findings: text("findings"),
    recommendations: text("recommendations"),
    dueDate: timestamp("due_date"),
    completedAt: timestamp("completed_at"),
    approvedAt: timestamp("approved_at"),
    submittedAt: timestamp("submitted_at"), // When assessment was submitted for review
    // Bi-annual review tracking fields
    nextReviewDate: timestamp("next_review_date"), // When the next review is due
    lastReviewedAt: timestamp("last_reviewed_at"), // When last review was completed
    lastReviewedById: varchar("last_reviewed_by_id").references(() => users.id), // Who performed last review
    reviewCount: integer("review_count").default(0), // Number of reviews completed
    isOverdueForReview: boolean("is_overdue_for_review").default(false), // Computed flag for quick filtering
    // Review progress tracking (for in-progress reviews)
    reviewProgressNotes: text("review_progress_notes"), // Notes saved during review
    reviewProgressSavedAt: timestamp("review_progress_saved_at"), // When progress was last saved
    reviewProgressSavedBy: varchar("review_progress_saved_by").references(() => users.id), // Who saved progress
    // Track which questions were changed during review cycle (for vCIO to review)
    changedQuestionIndexes: jsonb("changed_question_indexes"), // Array of question indexes that were changed
    previousResponses: jsonb("previous_responses"), // Responses before the review cycle changes (for before/after comparison)
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_assessments_organization").on(table.organizationId),
    index("idx_assessments_customer").on(table.customerId),
    index("idx_assessments_assigned_to").on(table.assignedToId),
    index("idx_assessments_vcio").on(table.vcioId), // Index for vCIO filtering
    index("idx_assessments_created_by").on(table.createdById),
    index("idx_assessments_next_review_date").on(table.nextReviewDate),
    index("idx_assessments_org_next_review").on(table.organizationId, table.nextReviewDate),
  ],
);

// Assessment review history table for bi-annual reviews
export const assessmentReviewHistory = pgTable(
  "assessment_review_history",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    assessmentId: integer("assessment_id")
      .references(() => assessments.id, { onDelete: "cascade" })
      .notNull(),
    reviewedById: varchar("reviewed_by_id")
      .references(() => users.id)
      .notNull(),
    reviewedAt: timestamp("reviewed_at").defaultNow().notNull(),
    questionsReviewed: integer("questions_reviewed").notNull(), // Count of questions that needed review
    questionsUpdated: integer("questions_updated").notNull(), // Count of questions actually updated
    previousReviewDate: timestamp("previous_review_date"), // Previous review date for tracking
    nextReviewDate: timestamp("next_review_date").notNull(), // Next scheduled review
    reviewNotes: text("review_notes"), // Optional notes from the reviewer
    reviewDuration: integer("review_duration"), // Time spent in minutes (optional)
    metadata: jsonb("metadata"), // Additional review metadata
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_review_history_assessment").on(table.assessmentId),
    index("idx_review_history_organization").on(table.organizationId),
    index("idx_review_history_reviewed_by").on(table.reviewedById),
    index("idx_review_history_reviewed_at").on(table.reviewedAt),
  ]
);

// Quarterly assessment review cycles table
export const assessmentReviewCycles = pgTable(
  "assessment_review_cycles",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    assessmentId: integer("assessment_id").references(() => assessments.id), // References the base/template assessment
    assignedToId: varchar("assigned_to_id")
      .references(() => users.id)
      .notNull(),
    cycle: varchar("cycle").notNull(), // Q1-2025, Q2-2025, Q3-2025, Q4-2025
    year: integer("year").notNull(),
    quarter: integer("quarter").notNull(), // 1-4: calendar quarter of the due date; the human-readable period (e.g. "August 2025", "H2 2025") lives in `cycle`
    status: varchar("status").notNull().default("scheduled"), // scheduled, in_progress, completed, overdue, cancelled
    scheduledStartDate: timestamp("scheduled_start_date"),
    scheduledReviewDate: timestamp("scheduled_review_date"), // When alignment engineer plans to start review
    actualStartDate: timestamp("actual_start_date"),
    completedDate: timestamp("completed_date"),
    reviewNotes: text("review_notes"), // Notes from the quarterly review
    changesRequired: boolean("changes_required").default(false), // Whether changes were needed
    changesSummary: text("changes_summary"), // Summary of what was changed
    alignmentScoreBefore: decimal("alignment_score_before", {
      precision: 5,
      scale: 2,
    }), // Score before review
    alignmentScoreAfter: decimal("alignment_score_after", {
      precision: 5,
      scale: 2,
    }), // Score after review
    improvementAreas: jsonb("improvement_areas"), // Areas identified for improvement
    dueDate: timestamp("due_date"), // Quarterly due date
    remindersSent: integer("reminders_sent").default(0),
    // Unified review pass: link to the completed assessment review outcome for this cycle's
    // assessment lens (additive, nullable — unified-review-cycle-evaluation 5.6, 5.1).
    assessmentReviewHistoryId: integer("assessment_review_history_id").references(
      () => assessmentReviewHistory.id,
      { onDelete: "set null" },
    ),
    // Unified review pass: link to the associated completed gap-analysis run for this cycle's
    // gap lens (additive, nullable — unified-review-cycle-evaluation 5.6, 5.2, 1.4, 6.1).
    // Lazy AnyPgColumn forward-reference form: techStackAudits is declared later and itself
    // references assessmentReviewCycles, so the explicit return type breaks the circular
    // type-inference cycle.
    gapAnalysisAuditId: integer("gap_analysis_audit_id").references(
      (): AnyPgColumn => techStackAudits.id,
      { onDelete: "set null" },
    ),
    // Unified review pass: optional notes/summary (additive; does NOT repurpose reviewNotes —
    // unified-review-cycle-evaluation 5.6).
    unifiedReviewNotes: text("unified_review_notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_review_cycles_organization").on(table.organizationId),
    index("idx_review_cycles_customer").on(table.customerId),
    index("idx_review_cycles_assigned_to").on(table.assignedToId),
    index("idx_review_cycles_status").on(table.status),
  ],
);

export const qbrs = pgTable(
  "qbrs",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    assessmentId: integer("assessment_id")
      .references(() => assessments.id)
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id)
      .notNull(),
    createdById: varchar("created_by_id")
      .references(() => users.id)
      .notNull(),
    assignedToId: varchar("assigned_to_id")
      .references(() => users.id)
      .notNull(), // Who is responsible for the QBR
    title: varchar("title").notNull(),
    quarter: varchar("quarter").notNull(), // DEPRECATED: Use reviewPeriod instead. Kept for backward compatibility
    reviewPeriod: varchar("review_period"), // Period label based on cadence: "Q1 2024" (quarterly), "January 2024" (monthly), "H1 2024" (biannual), "2024" (annual)
    executiveSummary: text("executive_summary"),
    keyFindings: jsonb("key_findings"), // Array of findings
    recommendations: jsonb("recommendations"), // Array of recommendations
    actionItems: jsonb("action_items"), // Array of action items
    pdfUrl: varchar("pdf_url"),
    status: varchar("status").notNull().default("draft"), // draft, finalized, scheduled, ready_for_presentation, presented, summary_sent, delivered
    dueDate: timestamp("due_date"), // Due date for QBR completion
    presentationDate: timestamp("presentation_date"), // Date when QBR was presented
    scheduledMeetingDate: timestamp("scheduled_meeting_date"), // Scheduled customer meeting date
    meetingLocation: varchar("meeting_location"), // Physical or virtual meeting location
    teamsMeetingId: varchar("teams_meeting_id"), // Microsoft Teams meeting ID
    meetingType: varchar("meeting_type").default("virtual"), // virtual, in_person, hybrid
    attendees: jsonb("attendees"), // Array of attendee objects with name, email, role
    teamsMeetingUrl: text("teams_meeting_url"), // Microsoft Teams join URL
    customerApprovals: jsonb("customer_approvals"), // Array of recommendation approvals from customer
    meetingNotes: text("meeting_notes"), // Notes from the customer presentation meeting
    followUpDate: timestamp("follow_up_date"), // Next scheduled follow-up date
    quarterlyBudget: decimal("quarterly_budget", { precision: 12, scale: 2 }), // Approved budget for this quarter
    budgetAllocation: jsonb("budget_allocation"), // Budget breakdown by recommendation category
    // Alignment score tracking for improvement metrics
    currentAlignmentScore: decimal("current_alignment_score", {
      precision: 5,
      scale: 2,
    }), // Current calculated alignment score
    previousAlignmentScore: decimal("previous_alignment_score", {
      precision: 5,
      scale: 2,
    }), // Previous QBR's alignment score for improvement calculation
    alignmentImprovement: decimal("alignment_improvement", {
      precision: 5,
      scale: 2,
    }), // Percentage improvement since last QBR
    quarterlyReviewCycleId: integer("quarterly_review_cycle_id").references(
      () => assessmentReviewCycles.id,
    ), // Links to quarterly review that generated this QBR
    // Business Review cadence automation fields
    previousQbrId: integer("previous_qbr_id"), // Link to previous QBR for historical continuity
    isScheduled: boolean("is_scheduled").default(false), // True if auto-scheduled by system
    scheduledBy: varchar("scheduled_by").default("system"), // "system" or user ID who scheduled it
    notificationsSent: jsonb("notifications_sent"), // Track which notifications were sent: {scheduled, reminder30, reminder14, reminder7, overdue}
    manuallyAdjusted: boolean("manually_adjusted").default(false), // Flag for manual date changes
    originalScheduledDate: timestamp("original_scheduled_date"), // Original date before manual adjustment
    nextReviewScheduledDate: timestamp("next_review_scheduled_date"), // Date/time for next review (set during presentation)
    calendarSequence: integer("calendar_sequence").default(0), // iCalendar SEQUENCE number for meeting updates
    summaryTone: varchar("summary_tone").default("professional"), // Tone used for AI-generated executive summary: professional, friendly, technical, concise, urgent
    presentationConfig: jsonb("presentation_config"), // Per-QBR presentation configuration: { slides: { title: true, ... }, slideOrder: [...] }
    includedAssets: jsonb("included_assets"), // Array of asset IDs included in this QBR for lifecycle review
    assetReplacementCosts: jsonb("asset_replacement_costs"), // Map of assetId to estimated replacement cost for QBR review: { "1": "5000", "2": "3000" }
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_qbrs_organization").on(table.organizationId),
    index("idx_qbrs_customer").on(table.customerId),
    index("idx_qbrs_assigned_to").on(table.assignedToId),
    index("idx_qbrs_previous_qbr").on(table.previousQbrId),
    index("idx_qbrs_scheduled_meeting_date").on(table.scheduledMeetingDate),
    index("idx_qbrs_is_scheduled").on(table.isScheduled),
  ],
);

// Forward-only alignment score snapshots used to build historical trend charts.
// One row is recorded per customer per day (deduped) so the dashboard's history
// enriches over time without backfilling synthetic data.
export const alignmentScoreSnapshots = pgTable(
  "alignment_score_snapshots",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    score: decimal("score", { precision: 5, scale: 2 }).notNull(),
    source: varchar("source").notNull().default("auto"), // auto, qbr, manual
    capturedAt: timestamp("captured_at").defaultNow().notNull(),
    // Calendar day of capture; enforces one snapshot per customer per day at the
    // DB level so concurrent dashboard loads can't create duplicate rows.
    capturedDate: date("captured_date")
      .notNull()
      .default(sql`CURRENT_DATE`),
  },
  (table) => [
    index("idx_alignment_snapshots_org").on(table.organizationId),
    index("idx_alignment_snapshots_customer").on(table.customerId),
    index("idx_alignment_snapshots_captured").on(table.capturedAt),
    uniqueIndex("uq_alignment_snapshots_org_customer_date").on(
      table.organizationId,
      table.customerId,
      table.capturedDate,
    ),
  ],
);

export const insertAlignmentScoreSnapshotSchema = createInsertSchema(
  alignmentScoreSnapshots,
).omit({ id: true, capturedAt: true, capturedDate: true });
export type InsertAlignmentScoreSnapshot = z.infer<
  typeof insertAlignmentScoreSnapshotSchema
>;
export type AlignmentScoreSnapshot = typeof alignmentScoreSnapshots.$inferSelect;

// Scheduler runs tracking table for monitoring automated schedulers
export const schedulerRuns = pgTable(
  "scheduler_runs",
  {
    id: serial("id").primaryKey(),
    schedulerType: varchar("scheduler_type").notNull(), // "business_review", "assessment_review"
    organizationId: uuid("organization_id").references(() => organizations.id, {
      onDelete: "cascade",
    }), // Organization being processed, NULL for cross-org schedulers
    runStartedAt: timestamp("run_started_at").defaultNow().notNull(),
    runCompletedAt: timestamp("run_completed_at"),
    status: varchar("status").notNull(), // "running", "completed", "failed"
    recordsProcessed: integer("records_processed").default(0),
    errorMessage: text("error_message"),
    metadata: jsonb("metadata"), // Additional run details: {scheduledReviews, notificationsSent, errors[]}
  },
  (table) => [
    index("idx_scheduler_runs_type_org").on(table.schedulerType, table.organizationId),
    index("idx_scheduler_runs_started_at").on(table.runStartedAt),
    index("idx_scheduler_runs_status").on(table.status, table.runStartedAt),
  ],
);

export const sops = pgTable(
  "sops",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id").references(() => organizations.id, {
      onDelete: "cascade",
    }), // null for global SOPs
    title: varchar("title").notNull(),
    category: varchar("category").notNull(), // alignment_engineer, vcio, general
    content: text("content").notNull(),
    tags: jsonb("tags"), // Array of tag strings
    version: varchar("version").notNull().default("1.0"),
    lastModifiedById: varchar("last_modified_by_id").references(() => users.id),
    isGlobal: boolean("is_global").default(false), // Global SOPs available to all organizations
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_sops_organization").on(table.organizationId),
    index("idx_sops_global").on(table.isGlobal),
  ],
);

// Onboarding tours for guided user experience
export const onboardingTours = pgTable("onboarding_tours", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull(),
  description: text("description").notNull(),
  category: varchar("category").notNull(), // getting-started, advanced-features, workflows, role-specific
  estimatedTime: integer("estimated_time").notNull(), // in minutes
  targetRoles: text("target_roles").array().default([]), // Show for specific roles
  autoStart: boolean("auto_start").default(false),
  isActive: boolean("is_active").default(true),
  priority: integer("priority").default(1), // Display order
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Individual tooltip steps within tours
export const tooltipSteps = pgTable("tooltip_steps", {
  id: serial("id").primaryKey(),
  tourId: integer("tour_id")
    .references(() => onboardingTours.id, { onDelete: "cascade" })
    .notNull(),
  stepOrder: integer("step_order").notNull(), // Order within the tour
  target: varchar("target").notNull(), // CSS selector for target element
  title: varchar("title").notNull(),
  content: text("content").notNull(),
  position: varchar("position").default("auto"), // top, bottom, left, right, auto
  action: varchar("action").default("none"), // click, hover, focus, none
  category: varchar("category").notNull(), // navigation, features, workflow, tips
  page: varchar("page"), // Show only on specific pages
  prerequisites: text("prerequisites").array().default([]), // Other step IDs to complete first
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// User progress tracking for onboarding
export const userOnboardingProgress = pgTable(
  "user_onboarding_progress",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    organizationId: uuid("organization_id").references(() => organizations.id, {
      onDelete: "cascade",
    }), // Organization-specific progress
    tourId: integer("tour_id")
      .references(() => onboardingTours.id, { onDelete: "cascade" })
      .notNull(),
    stepId: integer("step_id").references(() => tooltipSteps.id, {
      onDelete: "cascade",
    }),
    status: varchar("status").notNull(), // started, completed, skipped, dismissed
    completedAt: timestamp("completed_at"),
    skippedAt: timestamp("skipped_at"),
    dismissedAt: timestamp("dismissed_at"),
    lastActiveStep: integer("last_active_step"), // Track current step in tour
    metadata: jsonb("metadata").default({}), // Extra data like time spent, interactions
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_user_onboarding_user_organization").on(
      table.userId,
      table.organizationId,
    ),
    index("idx_user_onboarding_tour").on(table.tourId),
  ],
);

export const integrations = pgTable("integrations", {
  id: serial("id").primaryKey(),
  organizationId: uuid("organization_id")
    .references(() => organizations.id, { onDelete: "cascade" })
    .notNull(),
  name: varchar("name").notNull(),
  type: varchar("type").notNull(), // psa, documentation, monitoring
  provider: varchar("provider").notNull(), // connectwise, autotask, itglue, hudu
  status: varchar("status").notNull().default("inactive"), // active, inactive, error
  config: jsonb("config"), // Integration configuration
  syncSettings: jsonb("sync_settings").default({
    customerSync: true,
    opportunitySync: true,
    syncInterval: 3600, // seconds
    lastCustomerSync: null,
    lastOpportunitySync: null,
    // Customer sync filters
    customerFilters: {
      statuses: ['Active'], // Default to Active status
      types: ['Customer'], // Default to Customer type
    },
  }),
  lastSyncAt: timestamp("last_sync_at"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// PSA sync logs for tracking data flow
export const psaSyncLogs = pgTable("psa_sync_logs", {
  id: serial("id").primaryKey(),
  integrationId: integer("integration_id")
    .references(() => integrations.id)
    .notNull(),
  syncType: varchar("sync_type").notNull(), // customer_import, opportunity_export
  status: varchar("status").notNull(), // success, error, partial
  recordsProcessed: integer("records_processed").default(0),
  recordsSuccessful: integer("records_successful").default(0),
  recordsFailed: integer("records_failed").default(0),
  errorDetails: jsonb("error_details"),
  metadata: jsonb("metadata"), // Additional sync information
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
});

// Customer PSA mapping for tracking synced customers
export const customerPsaMapping = pgTable("customer_psa_mapping", {
  id: serial("id").primaryKey(),
  customerId: integer("customer_id")
    .references(() => customers.id)
    .notNull(),
  integrationId: integer("integration_id")
    .references(() => integrations.id)
    .notNull(),
  psaCustomerId: varchar("psa_customer_id").notNull(), // External PSA customer ID
  psaCustomerData: jsonb("psa_customer_data"), // Original PSA customer data
  lastSyncedAt: timestamp("last_synced_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Opportunity exports to PSA
export const opportunityExports = pgTable("opportunity_exports", {
  id: serial("id").primaryKey(),
  qbrId: integer("qbr_id")
    .references(() => qbrs.id)
    .notNull(),
  integrationId: integer("integration_id")
    .references(() => integrations.id)
    .notNull(),
  psaOpportunityId: varchar("psa_opportunity_id"), // External PSA opportunity ID
  recommendations: jsonb("recommendations").notNull(), // QBR recommendations exported
  status: varchar("status").notNull().default("pending"), // pending, exported, error
  psaResponse: jsonb("psa_response"), // Response from PSA API
  errorMessage: text("error_message"),
  exportedAt: timestamp("exported_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const psaOpportunities = pgTable("psa_opportunities", {
  id: serial("id").primaryKey(),
  integrationId: integer("integration_id").references(() => integrations.id),
  qbrId: integer("qbr_id").references(() => qbrs.id),
  psaOpportunityId: varchar("psa_opportunity_id").notNull(),
  opportunityName: varchar("opportunity_name").notNull(),
  customerId: integer("customer_id").references(() => customers.id),
  psaCustomerId: varchar("psa_customer_id"),
  value: decimal("value", { precision: 12, scale: 2 }),
  probability: integer("probability"), // percentage
  closeDate: timestamp("close_date"),
  stage: varchar("stage"),
  description: text("description"),
  recommendations: jsonb("recommendations"), // Original QBR recommendations
  psaOpportunityData: jsonb("psa_opportunity_data"), // Full PSA response
  status: varchar("status").notNull().default("active"), // active, closed_won, closed_lost
  lastSyncedAt: timestamp("last_synced_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// RMM customer mapping for NinjaOne and other RMM integrations
// Allows manual mapping between TechAlign customers and RMM organizations
// which may have different names (e.g., "Acme Corp" vs "Acme Corp LLC")
export const customerRmmMapping = pgTable(
  "customer_rmm_mapping",
  {
    id: serial("id").primaryKey(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    integrationId: integer("integration_id")
      .references(() => integrations.id, { onDelete: "cascade" })
      .notNull(),
    rmmOrganizationId: varchar("rmm_organization_id", { length: 100 }).notNull(), // External RMM org ID
    rmmOrganizationName: varchar("rmm_organization_name", { length: 255 }), // Cached name for display
    rmmOrganizationData: jsonb("rmm_organization_data"), // Full RMM organization data
    isAutoMatched: boolean("is_auto_matched").default(false), // True if system auto-matched
    matchConfidence: decimal("match_confidence", { precision: 5, scale: 2 }), // Confidence score 0-100
    lastSyncedAt: timestamp("last_synced_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_rmm_mapping_customer").on(table.customerId),
    index("idx_rmm_mapping_integration").on(table.integrationId),
    index("idx_rmm_mapping_rmm_org").on(table.rmmOrganizationId),
    index("idx_rmm_mapping_integration_customer").on(table.integrationId, table.customerId),
  ]
);

// RMM device sync tracking - links RMM devices to TechAlign assets
export const rmmDeviceSync = pgTable(
  "rmm_device_sync",
  {
    id: serial("id").primaryKey(),
    integrationId: integer("integration_id")
      .references(() => integrations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    assetId: integer("asset_id").references(() => assets.id, { onDelete: "set null" }), // Link to TechAlign asset
    rmmDeviceId: varchar("rmm_device_id", { length: 100 }).notNull(), // External RMM device ID
    rmmOrganizationId: varchar("rmm_organization_id", { length: 100 }).notNull(), // RMM org this device belongs to
    deviceName: varchar("device_name", { length: 255 }),
    deviceType: varchar("device_type", { length: 100 }), // WINDOWS_WORKSTATION, WINDOWS_SERVER, MAC, etc.
    deviceData: jsonb("device_data"), // Full device data from RMM
    syncStatus: varchar("sync_status", { length: 50 }).default("synced"), // synced, error, deleted
    lastSyncedAt: timestamp("last_synced_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_rmm_device_integration").on(table.integrationId),
    index("idx_rmm_device_customer").on(table.customerId),
    index("idx_rmm_device_asset").on(table.assetId),
    index("idx_rmm_device_rmm_org").on(table.rmmOrganizationId),
  ]
);

export const recommendationTemplates = pgTable(
  "recommendation_templates",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id").references(() => organizations.id, {
      onDelete: "cascade",
    }), // null for global templates
    title: varchar("title").notNull(),
    description: text("description").notNull(),
    category: varchar("category").notNull(), // security, infrastructure, compliance, general
    priority: varchar("priority").notNull().default("medium"), // high, medium, low
    timeline: varchar("timeline"), // "1-2 weeks", "1-3 months", etc.
    estimatedCost: varchar("estimated_cost"), // "Low ($)", "Medium ($$)", "High ($$$)"
    costDetails: jsonb("cost_details"), // Detailed cost breakdown: { implementation, licensing, training, maintenance }
    tags: jsonb("tags"), // Array of tag strings for matching to assessment questions
    targetQuestions: jsonb("target_questions"), // Array of question IDs or patterns this recommendation addresses
    usageCount: integer("usage_count").default(0), // Track how often this template is used
    averageSuccessRate: decimal("average_success_rate", {
      precision: 5,
      scale: 2,
    }), // % success rate across customers
    isGlobal: boolean("is_global").default(false), // Global templates available to all organizations
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_recommendation_templates_organization").on(table.organizationId),
    index("idx_recommendation_templates_global").on(table.isGlobal),
  ],
);

export const qbrComments = pgTable("qbr_comments", {
  id: serial("id").primaryKey(),
  qbrId: integer("qbr_id")
    .references(() => qbrs.id)
    .notNull(),
  userId: varchar("user_id")
    .references(() => users.id)
    .notNull(),
  content: text("content").notNull(),
  type: varchar("type").default("comment"), // comment, annotation
  section: varchar("section"), // executive_summary, key_findings, recommendations
  position: jsonb("position"), // For annotations: { x, y, width, height }
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const qbrCommentReplies = pgTable("qbr_comment_replies", {
  id: serial("id").primaryKey(),
  commentId: integer("comment_id")
    .references(() => qbrComments.id)
    .notNull(),
  userId: varchar("user_id")
    .references(() => users.id)
    .notNull(),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Customer quarterly budgets for planning and tracking
export const customerBudgets = pgTable(
  "customer_budgets",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    quarter: varchar("quarter").notNull(), // Q1 2024, Q2 2024, etc.
    totalBudget: decimal("total_budget", { precision: 12, scale: 2 }), // Total approved budget
    allocatedBudget: decimal("allocated_budget", {
      precision: 12,
      scale: 2,
    }).default("0"), // Budget assigned to recommendations
    spentBudget: decimal("spent_budget", { precision: 12, scale: 2 }).default(
      "0",
    ), // Actually spent budget
    budgetCategories: jsonb("budget_categories"), // Budget breakdown by category: { security: 5000, infrastructure: 10000 }
    approvalStatus: varchar("approval_status").default("pending"), // pending, approved, rejected
    approvedBy: varchar("approved_by").references(() => users.id), // User who approved the budget
    approvedAt: timestamp("approved_at"),
    notes: text("notes"), // Additional budget notes or constraints
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_customer_budgets_organization").on(table.organizationId),
    index("idx_customer_budgets_customer").on(table.customerId),
    index("idx_customer_budgets_quarter").on(table.quarter),
  ],
);

// Vendors table for managing IT service providers and suppliers
export const vendors = pgTable(
  "vendors",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    name: varchar("name").notNull(),
    contactName: varchar("contact_name"),
    contactEmail: varchar("contact_email"),
    contactPhone: varchar("contact_phone"),
    website: varchar("website"),
    accountManager: varchar("account_manager"),
    notes: text("notes"),
    totalAnnualSpend: decimal("total_annual_spend", { precision: 12, scale: 2 }),
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_vendors_organization").on(table.organizationId),
    index("idx_vendors_name").on(table.name),
  ],
);

// Customer budget line items for tracking specific IT expenses
export const customerBudgetLineItems = pgTable(
  "customer_budget_line_items",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    budgetId: integer("budget_id").references(() => customerBudgets.id, {
      onDelete: "cascade",
    }), // Optional link to quarterly budget
    category: varchar("category").notNull(), // m365_licenses, isp, cloud_services, hardware, software_licenses, support_contracts, security_services, backup_services, telecom, other
    name: varchar("name").notNull(), // e.g., "Microsoft 365 E3 Licenses", "Comcast Business Internet"
    description: text("description"), // Additional details about the expense
    vendor: varchar("vendor"), // e.g., "Microsoft", "Comcast", "AWS" (legacy field, use vendorId instead)
    vendorId: integer("vendor_id").references(() => vendors.id, { onDelete: "set null" }), // Reference to vendors table
    amount: decimal("amount", { precision: 12, scale: 2 }).notNull(), // Monthly or annual amount
    frequency: varchar("frequency").notNull().default("monthly"), // monthly, quarterly, annual, one-time
    startDate: timestamp("start_date"), // When this expense starts
    endDate: timestamp("end_date"), // When this expense ends (for contracts)
    renewalDate: timestamp("renewal_date"), // Next renewal date
    contractTerm: integer("contract_term"), // Contract length in months
    quantity: integer("quantity"), // Number of licenses/units
    unitCost: decimal("unit_cost", { precision: 12, scale: 2 }), // Cost per unit/license
    isRecurring: boolean("is_recurring").default(true), // Whether this is a recurring expense
    isActive: boolean("is_active").default(true), // Whether this line item is currently active
    notes: text("notes"), // Additional notes about this expense
    // Renewal tracking fields
    autoRenews: boolean("auto_renews").default(false), // Whether contract auto-renews
    renewalNotificationDays: jsonb("renewal_notification_days").default([90, 60, 30]), // Days before renewal to send notifications
    lastRenewalDate: timestamp("last_renewal_date"), // Date of last renewal
    renewalNotes: text("renewal_notes"), // Notes for vendor negotiation and renewal tracking
    tags: jsonb("tags"), // Array of tags for categorization
    metadata: jsonb("metadata"), // Additional structured data (contract details, etc.)
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_budget_line_items_organization").on(table.organizationId),
    index("idx_budget_line_items_customer").on(table.customerId),
    index("idx_budget_line_items_budget").on(table.budgetId),
    index("idx_budget_line_items_category").on(table.category),
    index("idx_budget_line_items_vendor").on(table.vendorId),
  ],
);

// QBR Recommendations assigned to quarters for budget planning
export const qbrRecommendations = pgTable(
  "qbr_recommendations",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    qbrId: integer("qbr_id")
      .references(() => qbrs.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    title: varchar("title").notNull(),
    description: text("description").notNull(),
    category: varchar("category").notNull(), // security, infrastructure, compliance, process, training
    priority: varchar("priority").notNull(), // critical, high, medium, low
    estimatedCost: decimal("estimated_cost", { precision: 12, scale: 2 }), // Estimated cost for this recommendation
    targetQuarter: varchar("target_quarter"), // Q1 2024, Q2 2024, etc. - when this should be implemented
    targetMonth: varchar("target_month"), // Jan 2024, Feb 2024, etc. - specific month within the quarter
    status: varchar("status").default("pending"), // pending, approved, in_progress, completed, deferred
    budgetAllocated: decimal("budget_allocated", {
      precision: 12,
      scale: 2,
    }).default("0"), // Budget allocated to this recommendation
    actualCost: decimal("actual_cost", { precision: 12, scale: 2 }).default(
      "0",
    ), // Actual cost spent
    assignedTo: varchar("assigned_to").references(() => users.id), // Person responsible for this recommendation
    reviewedBy: varchar("reviewed_by").references(() => users.id), // Who reviewed/approved this recommendation
    reviewedAt: timestamp("reviewed_at"),
    completedAt: timestamp("completed_at"),
    deferralReason: text("deferral_reason"), // Reason if deferred
    businessJustification: text("business_justification"), // Why this recommendation is important
    technicalRequirements: jsonb("technical_requirements"), // Technical specs, dependencies, etc.
    successMetrics: jsonb("success_metrics"), // How to measure success
    riskAssessment: text("risk_assessment"), // Risks if not implemented
    sourceIndex: integer("source_index"), // Index in the QBR JSON recommendations array for syncing
    // Nullable link to the durable customer risk this recommendation addresses.
    // Lazy reference form (() => customerRisks.id) defers resolution because
    // customerRisks is declared later in this file. The explicit AnyPgColumn
    // return type breaks the circular type-inference cycle between
    // qbrRecommendations and customerRisks (which references qbrRecommendations).
    riskId: integer("risk_id").references((): AnyPgColumn => customerRisks.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_qbr_recommendations_organization").on(table.organizationId),
    index("idx_qbr_recommendations_qbr").on(table.qbrId),
    index("idx_qbr_recommendations_customer").on(table.customerId),
    index("idx_qbr_recommendations_quarter").on(table.targetQuarter),
    index("idx_qbr_recommendations_status").on(table.status),
  ],
);

// Actual cost entries for tracking real spending against budget recommendations
export const actualCostEntries = pgTable(
  "actual_cost_entries",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    recommendationId: integer("recommendation_id")
      .references(() => qbrRecommendations.id, { onDelete: "cascade" }),
    lineItemId: integer("line_item_id")
      .references(() => customerBudgetLineItems.id, { onDelete: "cascade" }),
    amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
    entryDate: timestamp("entry_date").notNull(),
    description: text("description"),
    invoiceNumber: varchar("invoice_number"),
    enteredBy: varchar("entered_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_actual_cost_entries_organization").on(table.organizationId),
    index("idx_actual_cost_entries_recommendation").on(table.recommendationId),
    index("idx_actual_cost_entries_line_item").on(table.lineItemId),
    index("idx_actual_cost_entries_entry_date").on(table.entryDate),
  ],
);

// Budget history for tracking changes over time
export const budgetHistory = pgTable(
  "budget_history",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    year: varchar("year").notNull(),
    previousBudget: decimal("previous_budget", { precision: 12, scale: 2 }),
    newBudget: decimal("new_budget", { precision: 12, scale: 2 }).notNull(),
    changedBy: varchar("changed_by")
      .references(() => users.id)
      .notNull(),
    changeReason: text("change_reason"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_budget_history_organization").on(table.organizationId),
    index("idx_budget_history_customer").on(table.customerId),
    index("idx_budget_history_year").on(table.year),
  ],
);

// Report templates for customizable budget reports
export const reportTemplates = pgTable(
  "report_templates",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    name: varchar("name").notNull(),
    description: text("description"),
    templateType: varchar("template_type").notNull(), // executive_summary, variance_analysis, vendor_analysis, cost_optimization
    content: jsonb("content").notNull(), // Template structure with dynamic fields
    isDefault: boolean("is_default").default(false), // System default templates
    isActive: boolean("is_active").default(true),
    audience: varchar("audience").notNull().default("internal"), // "internal" | "customer-facing"
    sections: jsonb("sections").notNull().default([]),           // ReportSectionConfig[]
    createdBy: varchar("created_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_report_templates_organization").on(table.organizationId),
    index("idx_report_templates_type").on(table.templateType),
  ],
);

// Generated reports history
export const generatedReports = pgTable(
  "generated_reports",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" }),
    templateId: integer("template_id")
      .references(() => reportTemplates.id, { onDelete: "set null" }),
    reportType: varchar("report_type").notNull(), // executive_summary, variance_analysis, vendor_analysis, cost_optimization
    title: varchar("title").notNull(),
    dateRange: jsonb("date_range").notNull(), // { startDate, endDate }
    reportData: jsonb("report_data").notNull(), // Snapshot of data used in report
    pdfUrl: varchar("pdf_url"), // URL to generated PDF
    audience: varchar("audience").notNull().default("internal"), // "internal" | "customer-facing"
    sections: jsonb("sections"), // snapshot of section configs used
    status: varchar("status").notNull().default("completed"), // "generating" | "completed" | "failed"
    errorMessage: text("error_message"),
    generatedBy: varchar("generated_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_generated_reports_organization").on(table.organizationId),
    index("idx_generated_reports_customer").on(table.customerId),
    index("idx_generated_reports_type").on(table.reportType),
    index("idx_generated_reports_created_at").on(table.createdAt),
  ],
);

// Report schedules for recurring report generation
export const reportSchedules = pgTable(
  "report_schedules",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    templateId: integer("template_id")
      .references(() => reportTemplates.id, { onDelete: "cascade" })
      .notNull(),
    name: varchar("name").notNull(),
    cadence: varchar("cadence").notNull(), // "weekly" | "monthly" | "quarterly"
    filters: jsonb("filters").notNull(), // ReportFilters (customerIds, dateRangeType)
    recipients: jsonb("recipients").notNull(), // string[] of email addresses
    timezone: varchar("timezone").notNull().default("UTC"), // IANA timezone (e.g. "America/New_York")
    isActive: boolean("is_active").default(true),
    nextRunDate: timestamp("next_run_date").notNull(),
    lastRunDate: timestamp("last_run_date"),
    createdBy: varchar("created_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_report_schedules_org").on(table.organizationId),
    index("idx_report_schedules_next_run").on(table.nextRunDate),
  ],
);

// Report schedule run history for tracking each scheduled report execution
export const reportScheduleRuns = pgTable(
  "report_schedule_runs",
  {
    id: serial("id").primaryKey(),
    scheduleId: integer("schedule_id")
      .references(() => reportSchedules.id, { onDelete: "cascade" })
      .notNull(),
    generatedReportId: integer("generated_report_id")
      .references(() => generatedReports.id, { onDelete: "set null" }),
    status: varchar("status").notNull(), // "success" | "failed"
    errorMessage: text("error_message"),
    recipientCount: integer("recipient_count"),
    runAt: timestamp("run_at").defaultNow(),
  },
  (table) => [
    index("idx_report_schedule_runs_schedule").on(table.scheduleId),
  ],
);

// Budget templates for reusable budget configurations
export const budgetTemplates = pgTable(
  "budget_templates",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" }),
    name: varchar("name").notNull(),
    description: text("description"),
    industry: varchar("industry"), // healthcare, financial_services, education, legal, manufacturing, professional_services, technology, retail, government, nonprofit, other
    yearlyBudget: decimal("yearly_budget", { precision: 12, scale: 2 }), // Suggested annual budget amount
    lineItems: jsonb("line_items").notNull(), // Array of budget line item templates
    isGlobal: boolean("is_global").default(false), // Global templates available to all organizations
    createdBy: varchar("created_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_budget_templates_organization").on(table.organizationId),
    index("idx_budget_templates_global").on(table.isGlobal),
    index("idx_budget_templates_industry").on(table.industry),
  ],
);

// Reusable findings library for cross-QBR insights
export const findingsLibrary = pgTable(
  "findings_library",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    title: varchar("title").notNull(),
    description: text("description").notNull(),
    category: varchar("category").notNull(), // security, infrastructure, compliance, process, training
    severity: varchar("severity").notNull(), // critical, high, medium, low
    isTemplate: boolean("is_template").default(false), // Whether this is a reusable template
    sourceQbrId: integer("source_qbr_id").references(() => qbrs.id), // Original QBR where this finding was created
    usageCount: integer("usage_count").default(0), // How many times this finding has been reused
    tags: jsonb("tags"), // Array of tags for easy searching and filtering
    recommendedActions: jsonb("recommended_actions"), // Standard action items for this type of finding
    createdBy: varchar("created_by")
      .references(() => users.id)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_findings_library_organization").on(table.organizationId),
    index("idx_findings_library_category").on(table.category),
    index("idx_findings_library_template").on(table.isTemplate),
  ],
);

// Track which findings are used in which QBRs
export const qbrFindingsUsage = pgTable(
  "qbr_findings_usage",
  {
    id: serial("id").primaryKey(),
    qbrId: integer("qbr_id")
      .references(() => qbrs.id, { onDelete: "cascade" })
      .notNull(),
    findingId: integer("finding_id")
      .references(() => findingsLibrary.id, { onDelete: "cascade" })
      .notNull(),
    customContext: text("custom_context"), // Customer-specific context for this finding
    priority: varchar("priority").default("medium"), // high, medium, low - customer-specific priority
    status: varchar("status").default("open"), // open, in_progress, resolved, deferred
    assignedTo: varchar("assigned_to").references(() => users.id), // Person responsible for addressing this finding
    dueDate: timestamp("due_date"), // When this finding should be addressed
    // Nullable link to the durable customer risk this usage projects onto.
    // Lazy reference form (() => customerRisks.id) defers resolution because
    // customerRisks is declared later in this file.
    riskId: integer("risk_id").references(() => customerRisks.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_qbr_findings_qbr").on(table.qbrId),
    index("idx_qbr_findings_finding").on(table.findingId),
  ],
);

// Durable, customer-level risk records that persist across review cycles.
// Declared after qbrRecommendations / findingsLibrary / customerBudgetLineItems so
// its FK references resolve in declaration order.
export const customerRisks = pgTable(
  "customer_risks",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    title: varchar("title").notNull(), // xssSafeString
    description: text("description"), // xssSafeTextarea
    category: varchar("category").notNull(), // security, infrastructure, compliance, process, training
    severity: varchar("severity").notNull(), // critical, high, medium, low
    status: varchar("status").notNull().default("identified"), // identified, recommended, accepted, in_progress, resolved, deferred, risk_accepted
    sourceType: varchar("source_type"), // assessment, tech_stack_audit, m365, asset, manual
    sourceId: integer("source_id"), // id within the source system (nullable)
    originFindingId: integer("origin_finding_id").references(
      () => findingsLibrary.id,
    ), // optional link to the catalog finding type
    currentRecommendationId: integer("current_recommendation_id").references(
      () => qbrRecommendations.id,
    ), // optional; what currently addresses it
    budgetLineItemId: integer("budget_line_item_id").references(
      () => customerBudgetLineItems.id,
    ), // optional; what funds the fix
    metricKey: varchar("metric_key"), // e.g. m365_secure_score, alignment_score
    baselineValue: decimal("baseline_value", { precision: 12, scale: 2 }), // metric at identification
    targetValue: decimal("target_value", { precision: 12, scale: 2 }), // goal
    currentValue: decimal("current_value", { precision: 12, scale: 2 }), // latest observed
    assignedTo: varchar("assigned_to").references(() => users.id), // owner
    // --- Tech-stack gap intake fields (tech-stack-gap-recommendations spec, Req 4, 5, 7) ---
    // All additive + nullable/defaulted so existing risk rows remain valid.
    // recommendedStandardId / categoryId use the lazy AnyPgColumn forward-reference
    // form because techStackStandards and techStackCategories are declared later in
    // this file; the explicit return type breaks the circular type-inference cycle.
    recommendedStandardId: integer("recommended_standard_id").references(
      (): AnyPgColumn => techStackStandards.id,
      { onDelete: "set null" },
    ), // originating standard (gap → standard link)
    categoryId: integer("category_id").references(
      (): AnyPgColumn => techStackCategories.id,
    ), // for category-derived reconciliation
    provenance: varchar("provenance").default("drift"), // drift | standard_change (forward-compat)
    gapPriority: varchar("gap_priority"), // low | medium | high
    gapTiming: varchar("gap_timing"), // near_term | roadmap
    reconciliationKey: varchar("reconciliation_key"), // derived dedup key (std:{id} / cat:{id}:item:{name})
    identifiedAt: timestamp("identified_at").defaultNow(),
    resolvedAt: timestamp("resolved_at"), // set when status -> resolved
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_customer_risks_organization").on(table.organizationId),
    index("idx_customer_risks_customer").on(table.customerId),
    index("idx_customer_risks_status").on(table.status),
    index("idx_customer_risks_org_customer_status").on(
      table.organizationId,
      table.customerId,
      table.status,
    ),
    // Idempotent reconciliation (Req 7.1): at most one open gap intake row per
    // (organizationId, reconciliationKey) for tech-stack-audit gaps that are still
    // in an open lifecycle state. Partial so it only constrains gap rows and never
    // collides with other risk sources or terminal/promoted gaps.
    uniqueIndex("uq_customer_risks_gap_reconciliation")
      .on(table.organizationId, table.reconciliationKey)
      .where(
        sql`${table.sourceType} = 'tech_stack_audit' AND ${table.status} IN ('identified', 'proposed')`,
      ),
    // Provenance is constrained to exactly { drift, standard_change } at the DB
    // layer (standard-change-propagation spec, Req 4.3). NULL is permitted so
    // existing rows that predate the column (or default to 'drift') stay valid
    // with no backfill — Postgres CHECK treats a NULL result as non-violating.
    check(
      "chk_customer_risks_provenance",
      sql`${table.provenance} IS NULL OR ${table.provenance} IN ('drift', 'standard_change')`,
    ),
  ],
);

// Append-only history of every transition, funding decision, metric movement, or note
// against a customer risk. No updatedAt: events are never mutated by application code.
export const customerRiskEvents = pgTable(
  "customer_risk_events",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    riskId: integer("risk_id")
      .references(() => customerRisks.id, { onDelete: "cascade" })
      .notNull(),
    qbrId: integer("qbr_id").references(() => qbrs.id), // which review this happened in (nullable)
    eventType: varchar("event_type").notNull(), // status_change, funded, metric_update, note, reopened
    fromStatus: varchar("from_status"), // for status_change/reopened
    toStatus: varchar("to_status"), // for status_change/reopened
    metricValue: decimal("metric_value", { precision: 12, scale: 2 }), // for metric_update
    note: text("note"), // xssSafeTextarea
    proposedBy: varchar("proposed_by").default("user"), // user id, or agent:<name> (future)
    reviewStatus: varchar("review_status").default("approved"), // pending, approved, rejected, auto_applied
    confidence: decimal("confidence", { precision: 4, scale: 3 }), // 0-1, null for human events
    createdById: varchar("created_by_id").references(() => users.id), // actor (null when agent)
    createdAt: timestamp("created_at").defaultNow(), // append-only; no updatedAt
  },
  (table) => [
    index("idx_customer_risk_events_risk").on(table.riskId),
    index("idx_customer_risk_events_organization").on(table.organizationId),
    index("idx_customer_risk_events_qbr").on(table.qbrId),
    index("idx_customer_risk_events_review_status").on(table.reviewStatus),
  ],
);

// Notification type constants for explicit categorization
export const NotificationType = {
  // Assessment notifications
  ASSESSMENT_CREATED: 'assessment_created',
  ASSESSMENT_ASSIGNED: 'assessment_assigned',
  ASSESSMENT_COMPLETED: 'assessment_completed',
  ASSESSMENT_DEADLINE_APPROACHING: 'assessment_deadline_approaching',
  ASSESSMENT_OVERDUE: 'assessment_overdue',
  ASSESSMENT_REVIEW_REQUIRED: 'assessment_review_required',
  ASSESSMENT_REVIEW_DUE: 'assessment_review_due',
  ASSESSMENT_REVIEW_REMINDER: 'assessment_review_reminder',
  ASSESSMENT_REVIEW_OVERDUE: 'assessment_review_overdue',
  ASSESSMENT_REVIEW_COMPLETED: 'assessment_review_completed',
  ASSESSMENT_APPROVED: 'assessment_approved',
  ASSESSMENT_REASSIGNED: 'assessment_reassigned',
  VCIO_ASSIGNED: 'vcio_assigned',
  VCIO_REVIEW_READY: 'vcio_review_ready',
  
  // QBR/Business Review notifications
  QBR_GENERATED: 'qbr_generated',
  QBR_READY: 'qbr_ready',
  QBR_DELIVERED: 'qbr_delivered',
  QBR_SCHEDULED: 'qbr_scheduled',
  QBR_COMMENT_ADDED: 'qbr_comment_added',
  BUSINESS_REVIEW_SCHEDULED: 'business_review_scheduled',
  BUSINESS_REVIEW_UPCOMING: 'business_review_upcoming',
  BUSINESS_REVIEW_OVERDUE: 'business_review_overdue',
  
  // Customer notifications
  CUSTOMER_ADDED: 'customer_added',
  
  // Template notifications
  TEMPLATE_CREATED: 'template_created',
  
  // User management notifications
  USER_ROLE_CHANGED: 'user_role_changed',
  NEW_USER_ADDED: 'new_user_added',
  
  // System notifications
  SYSTEM_UPDATE: 'system_update',
  SYSTEM_MAINTENANCE: 'system_maintenance',
  BACKUP_COMPLETED: 'backup_completed',
  BACKUP_FAILED: 'backup_failed',
  DATA_EXPORT_READY: 'data_export_ready',
  
  // Security notifications
  SECURITY_EVENT: 'security_event',
  NEW_DEVICE_LOGIN: 'new_device_login',
  
  // Asset lifecycle notifications
  ASSET_WARRANTY_EXPIRING: 'asset_warranty_expiring',
  ASSET_WARRANTY_EXPIRED: 'asset_warranty_expired',
  ASSET_EOL_APPROACHING: 'asset_eol_approaching',
  ASSET_EOL_REACHED: 'asset_eol_reached',
  ASSET_REPLACEMENT_DUE: 'asset_replacement_due',
  ASSET_ALERT: 'asset_alert',
  
  // Generic
  ERROR: 'error',
  INFO: 'info',
} as const;

export type NotificationTypeValue = typeof NotificationType[keyof typeof NotificationType];

// Map notification types to user preference keys
export const NOTIFICATION_PREFERENCE_MAP: Record<string, string> = {
  [NotificationType.ASSESSMENT_CREATED]: 'assessmentAssignments',
  [NotificationType.ASSESSMENT_ASSIGNED]: 'assessmentAssignments',
  [NotificationType.ASSESSMENT_COMPLETED]: 'assessmentApprovals',
  [NotificationType.ASSESSMENT_DEADLINE_APPROACHING]: 'assessmentDeadlines',
  [NotificationType.ASSESSMENT_OVERDUE]: 'assessmentDeadlines',
  [NotificationType.ASSESSMENT_REVIEW_REQUIRED]: 'assessmentSubmissions',
  [NotificationType.ASSESSMENT_REVIEW_DUE]: 'assessmentReviews',
  [NotificationType.ASSESSMENT_REVIEW_REMINDER]: 'assessmentReviews',
  [NotificationType.ASSESSMENT_REVIEW_OVERDUE]: 'assessmentReviews',
  [NotificationType.ASSESSMENT_REVIEW_COMPLETED]: 'assessmentReviews',
  [NotificationType.ASSESSMENT_APPROVED]: 'assessmentApprovals',
  [NotificationType.ASSESSMENT_REASSIGNED]: 'assessmentAssignments',
  [NotificationType.VCIO_ASSIGNED]: 'assessmentAssignments',
  [NotificationType.VCIO_REVIEW_READY]: 'assessmentSubmissions',
  [NotificationType.QBR_GENERATED]: 'qbrGeneration',
  [NotificationType.QBR_READY]: 'qbrGeneration',
  [NotificationType.QBR_DELIVERED]: 'qbrGeneration',
  [NotificationType.QBR_SCHEDULED]: 'qbrGeneration',
  [NotificationType.QBR_COMMENT_ADDED]: 'qbrGeneration',
  [NotificationType.BUSINESS_REVIEW_SCHEDULED]: 'qbrGeneration',
  [NotificationType.BUSINESS_REVIEW_UPCOMING]: 'qbrGeneration',
  [NotificationType.BUSINESS_REVIEW_OVERDUE]: 'qbrGeneration',
  [NotificationType.CUSTOMER_ADDED]: 'customerUpdates',
  [NotificationType.TEMPLATE_CREATED]: 'templateUpdates',
  [NotificationType.USER_ROLE_CHANGED]: 'userManagement',
  [NotificationType.NEW_USER_ADDED]: 'newUserAdded',
  [NotificationType.SYSTEM_UPDATE]: 'systemMaintenance',
  [NotificationType.SYSTEM_MAINTENANCE]: 'systemMaintenance',
  [NotificationType.BACKUP_COMPLETED]: 'systemMaintenance',
  [NotificationType.BACKUP_FAILED]: 'systemMaintenance',
  [NotificationType.DATA_EXPORT_READY]: 'systemMaintenance',
  [NotificationType.SECURITY_EVENT]: 'securityAlerts',
  [NotificationType.NEW_DEVICE_LOGIN]: 'securityAlerts',
  [NotificationType.ASSET_WARRANTY_EXPIRING]: 'assetAlerts',
  [NotificationType.ASSET_WARRANTY_EXPIRED]: 'assetAlerts',
  [NotificationType.ASSET_EOL_APPROACHING]: 'assetAlerts',
  [NotificationType.ASSET_EOL_REACHED]: 'assetAlerts',
  [NotificationType.ASSET_REPLACEMENT_DUE]: 'assetAlerts',
  [NotificationType.ASSET_ALERT]: 'assetAlerts',
};

export const notifications = pgTable(
  "notifications",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id").references(() => organizations.id, {
      onDelete: "cascade",
    }), // null for global notifications
    userId: varchar("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    type: varchar("type").notNull(), // info, success, warning, error (display type)
    notificationType: varchar("notification_type"), // explicit categorization (e.g., 'assessment_assigned')
    title: varchar("title").notNull(),
    message: text("message").notNull(),
    isRead: boolean("is_read").default(false),
    actionUrl: varchar("action_url"), // Optional URL for action buttons
    relatedId: varchar("related_id"), // ID of related entity (assessment, QBR, etc.)
    relatedType: varchar("related_type"), // Type of related entity ('assessment', 'qbr', etc.)
    metadata: jsonb("metadata"), // Additional notification data
    emailSentAt: timestamp("email_sent_at"), // Track when email was sent (null if not sent)
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_notifications_organization_user").on(
      table.organizationId,
      table.userId,
    ),
    index("idx_notifications_user").on(table.userId),
    index("idx_notifications_type").on(table.notificationType),
  ],
);

// Relations
export const usersRelations = relations(users, ({ many }) => ({
  assignedAssessments: many(assessments, { relationName: "assignedTo" }),
  reviewedAssessments: many(assessments, { relationName: "reviewedBy" }),
  createdQbrs: many(qbrs, { relationName: "createdBy" }),
  assignedQbrs: many(qbrs, { relationName: "assignedTo" }),
}));

export const customersRelations = relations(customers, ({ many }) => ({
  assessments: many(assessments),
  qbrs: many(qbrs),
  reviewCycles: many(assessmentReviewCycles),
}));

export const assessmentTemplatesRelations = relations(
  assessmentTemplates,
  ({ many }) => ({
    assessments: many(assessments),
  }),
);

export const assessmentsRelations = relations(assessments, ({ one, many }) => ({
  customer: one(customers, {
    fields: [assessments.customerId],
    references: [customers.id],
  }),
  template: one(assessmentTemplates, {
    fields: [assessments.templateId],
    references: [assessmentTemplates.id],
  }),
  assignedTo: one(users, {
    fields: [assessments.assignedToId],
    references: [users.id],
    relationName: "assignedTo",
  }),
  reviewedBy: one(users, {
    fields: [assessments.reviewedById],
    references: [users.id],
    relationName: "reviewedBy",
  }),
  lastReviewedBy: one(users, {
    fields: [assessments.lastReviewedById],
    references: [users.id],
    relationName: "lastReviewedBy",
  }),
  qbrs: many(qbrs),
  reviewHistory: many(assessmentReviewHistory),
}));

export const qbrsRelations = relations(qbrs, ({ one, many }) => ({
  assessment: one(assessments, {
    fields: [qbrs.assessmentId],
    references: [assessments.id],
  }),
  customer: one(customers, {
    fields: [qbrs.customerId],
    references: [customers.id],
  }),
  createdBy: one(users, {
    fields: [qbrs.createdById],
    references: [users.id],
    relationName: "createdBy",
  }),
  assignedTo: one(users, {
    fields: [qbrs.assignedToId],
    references: [users.id],
    relationName: "assignedTo",
  }),
  comments: many(qbrComments),
}));

export const qbrCommentsRelations = relations(qbrComments, ({ one, many }) => ({
  qbr: one(qbrs, {
    fields: [qbrComments.qbrId],
    references: [qbrs.id],
  }),
  user: one(users, {
    fields: [qbrComments.userId],
    references: [users.id],
  }),
  replies: many(qbrCommentReplies),
}));

export const qbrCommentRepliesRelations = relations(
  qbrCommentReplies,
  ({ one }) => ({
    comment: one(qbrComments, {
      fields: [qbrCommentReplies.commentId],
      references: [qbrComments.id],
    }),
    user: one(users, {
      fields: [qbrCommentReplies.userId],
      references: [users.id],
    }),
  }),
);

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, {
    fields: [notifications.userId],
    references: [users.id],
  }),
}));

export const customerBudgetsRelations = relations(
  customerBudgets,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [customerBudgets.organizationId],
      references: [organizations.id],
    }),
    customer: one(customers, {
      fields: [customerBudgets.customerId],
      references: [customers.id],
    }),
    approver: one(users, {
      fields: [customerBudgets.approvedBy],
      references: [users.id],
    }),
    lineItems: many(customerBudgetLineItems),
  }),
);

export const vendorsRelations = relations(vendors, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [vendors.organizationId],
    references: [organizations.id],
  }),
  lineItems: many(customerBudgetLineItems),
}));

export const customerBudgetLineItemsRelations = relations(
  customerBudgetLineItems,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [customerBudgetLineItems.organizationId],
      references: [organizations.id],
    }),
    customer: one(customers, {
      fields: [customerBudgetLineItems.customerId],
      references: [customers.id],
    }),
    budget: one(customerBudgets, {
      fields: [customerBudgetLineItems.budgetId],
      references: [customerBudgets.id],
    }),
    vendor: one(vendors, {
      fields: [customerBudgetLineItems.vendorId],
      references: [vendors.id],
    }),
  }),
);

export const findingsLibraryRelations = relations(
  findingsLibrary,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [findingsLibrary.organizationId],
      references: [organizations.id],
    }),
    sourceQbr: one(qbrs, {
      fields: [findingsLibrary.sourceQbrId],
      references: [qbrs.id],
    }),
    creator: one(users, {
      fields: [findingsLibrary.createdBy],
      references: [users.id],
    }),
    usages: many(qbrFindingsUsage),
  }),
);

export const qbrFindingsUsageRelations = relations(
  qbrFindingsUsage,
  ({ one }) => ({
    qbr: one(qbrs, { fields: [qbrFindingsUsage.qbrId], references: [qbrs.id] }),
    finding: one(findingsLibrary, {
      fields: [qbrFindingsUsage.findingId],
      references: [findingsLibrary.id],
    }),
    assignee: one(users, {
      fields: [qbrFindingsUsage.assignedTo],
      references: [users.id],
    }),
    risk: one(customerRisks, {
      fields: [qbrFindingsUsage.riskId],
      references: [customerRisks.id],
    }),
  }),
);

export const qbrRecommendationsRelations = relations(
  qbrRecommendations,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [qbrRecommendations.organizationId],
      references: [organizations.id],
    }),
    qbr: one(qbrs, {
      fields: [qbrRecommendations.qbrId],
      references: [qbrs.id],
    }),
    customer: one(customers, {
      fields: [qbrRecommendations.customerId],
      references: [customers.id],
    }),
    assignee: one(users, {
      fields: [qbrRecommendations.assignedTo],
      references: [users.id],
    }),
    reviewer: one(users, {
      fields: [qbrRecommendations.reviewedBy],
      references: [users.id],
    }),
    risk: one(customerRisks, {
      fields: [qbrRecommendations.riskId],
      references: [customerRisks.id],
    }),
  }),
);

export const customerRisksRelations = relations(
  customerRisks,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [customerRisks.organizationId],
      references: [organizations.id],
    }),
    customer: one(customers, {
      fields: [customerRisks.customerId],
      references: [customers.id],
    }),
    originFinding: one(findingsLibrary, {
      fields: [customerRisks.originFindingId],
      references: [findingsLibrary.id],
    }),
    currentRecommendation: one(qbrRecommendations, {
      fields: [customerRisks.currentRecommendationId],
      references: [qbrRecommendations.id],
    }),
    budgetLineItem: one(customerBudgetLineItems, {
      fields: [customerRisks.budgetLineItemId],
      references: [customerBudgetLineItems.id],
    }),
    assignee: one(users, {
      fields: [customerRisks.assignedTo],
      references: [users.id],
    }),
    events: many(customerRiskEvents),
  }),
);

export const customerRiskEventsRelations = relations(
  customerRiskEvents,
  ({ one }) => ({
    risk: one(customerRisks, {
      fields: [customerRiskEvents.riskId],
      references: [customerRisks.id],
    }),
    qbr: one(qbrs, {
      fields: [customerRiskEvents.qbrId],
      references: [qbrs.id],
    }),
    creator: one(users, {
      fields: [customerRiskEvents.createdById],
      references: [users.id],
    }),
  }),
);

// --- Customer Risk Register: Zod insert/update schemas and inferred types ---

// Review-status values for risk events. No exported constant tuple exists for these
// (they are local to the event model), so the allowed set is declared inline here.
const RISK_REVIEW_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "auto_applied",
] as const;

export const insertCustomerRiskSchema = createInsertSchema(customerRisks)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    title: xssSafeString(200),
    description: xssSafeTextarea(2000).optional().nullable(),
    category: z.enum(RISK_CATEGORIES),
    severity: z.enum(RISK_SEVERITIES),
    status: z.enum(RISK_STATUSES).default("identified"),
    sourceType: z.enum(RISK_SOURCE_TYPES).optional().nullable(),
  });

export const updateCustomerRiskSchema = createInsertSchema(customerRisks)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    organizationId: true,
  })
  .extend({
    title: xssSafeString(200),
    description: xssSafeTextarea(2000).optional().nullable(),
    category: z.enum(RISK_CATEGORIES),
    severity: z.enum(RISK_SEVERITIES),
    status: z.enum(RISK_STATUSES),
    sourceType: z.enum(RISK_SOURCE_TYPES).optional().nullable(),
  })
  .partial();

export const insertCustomerRiskEventSchema = createInsertSchema(
  customerRiskEvents,
)
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    eventType: z.enum(RISK_EVENT_TYPES),
    note: xssSafeTextarea(2000).optional().nullable(),
    reviewStatus: z.enum(RISK_REVIEW_STATUSES).default("approved"),
    // proposedBy is intentionally a relaxed string (default "user") rather than a
    // strict enum: it carries either a human value ("user") or a future
    // "agent:<name>" identifier, so a closed enum would be too restrictive.
    proposedBy: z.string().max(100).default("user"),
  });

export type CustomerRisk = typeof customerRisks.$inferSelect;
export type InsertCustomerRisk = z.infer<typeof insertCustomerRiskSchema>;
export type UpdateCustomerRisk = z.infer<typeof updateCustomerRiskSchema>;
export type CustomerRiskEvent = typeof customerRiskEvents.$inferSelect;
export type InsertCustomerRiskEvent = z.infer<
  typeof insertCustomerRiskEventSchema
>;

export const budgetHistoryRelations = relations(budgetHistory, ({ one }) => ({
  organization: one(organizations, {
    fields: [budgetHistory.organizationId],
    references: [organizations.id],
  }),
  customer: one(customers, {
    fields: [budgetHistory.customerId],
    references: [customers.id],
  }),
  changedByUser: one(users, {
    fields: [budgetHistory.changedBy],
    references: [users.id],
  }),
}));

export const reportTemplatesRelations = relations(
  reportTemplates,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [reportTemplates.organizationId],
      references: [organizations.id],
    }),
    creator: one(users, {
      fields: [reportTemplates.createdBy],
      references: [users.id],
    }),
    generatedReports: many(generatedReports),
  }),
);

export const generatedReportsRelations = relations(
  generatedReports,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [generatedReports.organizationId],
      references: [organizations.id],
    }),
    customer: one(customers, {
      fields: [generatedReports.customerId],
      references: [customers.id],
    }),
    template: one(reportTemplates, {
      fields: [generatedReports.templateId],
      references: [reportTemplates.id],
    }),
    generator: one(users, {
      fields: [generatedReports.generatedBy],
      references: [users.id],
    }),
  }),
);

export const reportSchedulesRelations = relations(
  reportSchedules,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [reportSchedules.organizationId],
      references: [organizations.id],
    }),
    template: one(reportTemplates, {
      fields: [reportSchedules.templateId],
      references: [reportTemplates.id],
    }),
    creator: one(users, {
      fields: [reportSchedules.createdBy],
      references: [users.id],
    }),
  }),
);

export const reportScheduleRunsRelations = relations(
  reportScheduleRuns,
  ({ one }) => ({
    schedule: one(reportSchedules, {
      fields: [reportScheduleRuns.scheduleId],
      references: [reportSchedules.id],
    }),
    generatedReport: one(generatedReports, {
      fields: [reportScheduleRuns.generatedReportId],
      references: [generatedReports.id],
    }),
  }),
);

export const budgetTemplatesRelations = relations(
  budgetTemplates,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [budgetTemplates.organizationId],
      references: [organizations.id],
    }),
    creator: one(users, {
      fields: [budgetTemplates.createdBy],
      references: [users.id],
    }),
  }),
);

export const assessmentReviewHistoryRelations = relations(
  assessmentReviewHistory,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [assessmentReviewHistory.organizationId],
      references: [organizations.id],
    }),
    assessment: one(assessments, {
      fields: [assessmentReviewHistory.assessmentId],
      references: [assessments.id],
    }),
    reviewedBy: one(users, {
      fields: [assessmentReviewHistory.reviewedById],
      references: [users.id],
    }),
  }),
);

export const assessmentReviewCyclesRelations = relations(
  assessmentReviewCycles,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [assessmentReviewCycles.organizationId],
      references: [organizations.id],
    }),
    customer: one(customers, {
      fields: [assessmentReviewCycles.customerId],
      references: [customers.id],
    }),
    assessment: one(assessments, {
      fields: [assessmentReviewCycles.assessmentId],
      references: [assessments.id],
    }),
    assignedTo: one(users, {
      fields: [assessmentReviewCycles.assignedToId],
      references: [users.id],
    }),
  }),
);

// Insert schemas with XSS protection
export const insertCustomerSchema = createInsertSchema(customers)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(100),
    type: xssSafeString(50),
    streetAddress: xssSafeString(200).optional(),
    city: xssSafeString(100).optional(),
    state: xssSafeString(100).optional(),
    zipCode: xssSafeString(20).optional(),
    country: xssSafeString(100).optional(),
    industry: z.enum(CUSTOMER_INDUSTRIES).optional(),
    companySize: xssSafeString(50).optional(),
    website: xssSafeUrl().optional(),
    notes: xssSafeTextarea(1000).optional(),
    yearlyBudgets: z.record(z.string(), z.number()).optional(),
    reviewFrequency: z.enum(["monthly", "quarterly", "biannual", "annual"]).optional(),
  });

// Customer contacts schema
export const insertCustomerContactSchema = createInsertSchema(customerContacts)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    firstName: xssSafeString(100),
    lastName: xssSafeString(100),
    email: xssSafeEmail().optional().nullable(),
    phone: xssSafePhone().optional().nullable(),
    mobile: xssSafePhone().optional().nullable(),
    jobTitle: z.preprocess(
      (val) => (val === "" || val === null || val === undefined) ? null : val,
      xssSafeString(150).nullable().optional()
    ),
    department: z.preprocess(
      (val) => (val === "" || val === null || val === undefined) ? null : val,
      xssSafeString(100).nullable().optional()
    ),
    notes: xssSafeTextarea(1000).optional().nullable(),
  });

export const updateCustomerContactSchema = insertCustomerContactSchema
  .omit({ organizationId: true, customerId: true })
  .partial();

export const insertAssessmentTemplateSchema = createInsertSchema(
  assessmentTemplates,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(200),
    description: xssSafeTextarea(1000).optional(),
    category: xssSafeString(100),
  });

export const insertAssessmentSchema = createInsertSchema(assessments)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    title: xssSafeString(200),
    notes: xssSafeTextarea(2000).optional(),
    vcioId: z.string().optional().nullable(), // Optional vCIO assignment
  });

export const updateAssessmentSchema = createInsertSchema(assessments)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    organizationId: true,
  })
  .extend({
    title: xssSafeString(200),
    notes: xssSafeTextarea(2000).optional(),
  })
  .partial();

export const insertQbrSchema = createInsertSchema(qbrs)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    title: xssSafeString(200),
    executiveSummary: xssSafeTextarea(5000).optional(),
    keyFindings: z
      .array(
        z.object({
          id: z.string(),
          title: xssSafeString(200),
          description: xssSafeTextarea(1000),
          severity: z.enum(["low", "medium", "high", "critical"]),
          category: xssSafeString(100),
        }),
      )
      .optional(),
    recommendations: z
      .array(
        z.object({
          id: z.string(),
          title: xssSafeString(200),
          description: xssSafeTextarea(1000),
          priority: z.enum(["low", "medium", "high", "critical"]),
          timeline: xssSafeString(100),
          estimatedCost: z.string().nullable().optional(),
          targetQuarter: z.string().nullable().optional(),
          targetMonth: z.string().nullable().optional(),
          budgetAllocated: z.union([z.string(), z.number()]).nullable().optional(),
          businessImpact: z.string().nullable().optional(),
        }),
      )
      .optional(),
    assetReplacementCosts: z.record(z.string(), z.string()).optional(),
    notes: xssSafeTextarea(2000).optional(),
    status: z.enum([
      "draft",
      "finalized",
      "scheduled",
      "ready_for_presentation",
      "presented",
      "summary_sent",
      "delivered",
    ]),
  });

export const insertSopSchema = createInsertSchema(sops)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    title: xssSafeString(200),
    content: xssSafeTextarea(10000),
    category: xssSafeString(100),
    tags: z.array(xssSafeString(50)).optional(),
  });

export const insertIntegrationSchema = createInsertSchema(integrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPsaSyncLogSchema = createInsertSchema(psaSyncLogs).omit({
  id: true,
  startedAt: true,
});

export const insertCustomerPsaMappingSchema = createInsertSchema(
  customerPsaMapping,
).omit({
  id: true,
  createdAt: true,
});

export const insertCustomerRmmMappingSchema = createInsertSchema(
  customerRmmMapping,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertRmmDeviceSyncSchema = createInsertSchema(
  rmmDeviceSync,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertOpportunityExportSchema = createInsertSchema(
  opportunityExports,
).omit({
  id: true,
  createdAt: true,
});

export const insertRecommendationTemplateSchema = createInsertSchema(
  recommendationTemplates,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertRolePermissionSchema = createInsertSchema(
  rolePermissions,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type UpsertUser = typeof users.$inferInsert & {
  roles?: string[];
};
export type LocalUser = {
  email: string;
  firstName?: string;
  lastName?: string;
  passwordHash: string;
  roles: string[];
  emailVerified?: boolean;
  isOwner?: boolean;
};
export type User = typeof users.$inferSelect & {
  /**
   * Compatibility fields. Some auth/role code paths read a `roles` array (derived
   * from `role`) or a snake_case `is_superadmin` flag whose runtime value may be a
   * Postgres boolean represented as `true`, `'t'`, or `1`. These are optional and
   * not backed by dedicated columns.
   */
  roles?: string[];
  is_superadmin?: boolean | string | number | null;
};
export type RolePermission = typeof rolePermissions.$inferSelect;
export const insertQbrCommentSchema = createInsertSchema(qbrComments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertQbrCommentReplySchema = createInsertSchema(
  qbrCommentReplies,
).omit({
  id: true,
  createdAt: true,
});

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCustomerBudgetSchema = createInsertSchema(
  customerBudgets,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertVendorSchema = createInsertSchema(vendors).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCustomerBudgetLineItemSchema = createInsertSchema(
  customerBudgetLineItems,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertActualCostEntrySchema = createInsertSchema(
  actualCostEntries,
).omit({
  id: true,
  createdAt: true,
});

export const insertFindingsLibrarySchema = createInsertSchema(
  findingsLibrary,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertQbrFindingsUsageSchema = createInsertSchema(
  qbrFindingsUsage,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertOnboardingTourSchema = createInsertSchema(
  onboardingTours,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTooltipStepSchema = createInsertSchema(tooltipSteps).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertUserOnboardingProgressSchema = createInsertSchema(
  userOnboardingProgress,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertAssessmentReviewHistorySchema = createInsertSchema(
  assessmentReviewHistory,
).omit({
  id: true,
  createdAt: true,
});

export const insertAssessmentReviewCycleSchema = createInsertSchema(
  assessmentReviewCycles,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Organization-related schemas and types
export const insertOrganizationSchema = createInsertSchema(organizations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertRolePermission = typeof rolePermissions.$inferInsert;
export type InsertCustomer = z.infer<typeof insertCustomerSchema>;
export type Customer = typeof customers.$inferSelect;
export type InsertCustomerContact = z.infer<typeof insertCustomerContactSchema>;
export type UpdateCustomerContact = z.infer<typeof updateCustomerContactSchema>;
export type CustomerContact = typeof customerContacts.$inferSelect;
export type InsertAssessmentTemplate = z.infer<
  typeof insertAssessmentTemplateSchema
>;
export type AssessmentTemplate = typeof assessmentTemplates.$inferSelect;
export type InsertAssessment = z.infer<typeof insertAssessmentSchema>;
export type UpdateAssessment = z.infer<typeof updateAssessmentSchema>;
export type Assessment = typeof assessments.$inferSelect;
export type InsertQbr = z.infer<typeof insertQbrSchema>;
export type Qbr = typeof qbrs.$inferSelect;
export type SchedulerRun = typeof schedulerRuns.$inferSelect;
export type InsertSop = z.infer<typeof insertSopSchema>;
export type Sop = typeof sops.$inferSelect;
export type InsertIntegration = z.infer<typeof insertIntegrationSchema>;
export type Integration = typeof integrations.$inferSelect;
export type InsertPsaSyncLog = z.infer<typeof insertPsaSyncLogSchema>;
export type PsaSyncLog = typeof psaSyncLogs.$inferSelect;
export type InsertCustomerPsaMapping = z.infer<
  typeof insertCustomerPsaMappingSchema
>;
export type CustomerPsaMapping = typeof customerPsaMapping.$inferSelect;
export type InsertCustomerRmmMapping = z.infer<
  typeof insertCustomerRmmMappingSchema
>;
export type CustomerRmmMapping = typeof customerRmmMapping.$inferSelect;
export type InsertRmmDeviceSync = z.infer<typeof insertRmmDeviceSyncSchema>;
export type RmmDeviceSync = typeof rmmDeviceSync.$inferSelect;
export type InsertOpportunityExport = z.infer<
  typeof insertOpportunityExportSchema
>;
export type OpportunityExport = typeof opportunityExports.$inferSelect;
export type InsertRecommendationTemplate = z.infer<
  typeof insertRecommendationTemplateSchema
>;
export type RecommendationTemplate =
  typeof recommendationTemplates.$inferSelect;
export type InsertQbrComment = z.infer<typeof insertQbrCommentSchema>;
export type QbrComment = typeof qbrComments.$inferSelect;
export type InsertQbrCommentReply = z.infer<typeof insertQbrCommentReplySchema>;
export type QbrCommentReply = typeof qbrCommentReplies.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;
export type InsertVendor = z.infer<typeof insertVendorSchema>;
export type Vendor = typeof vendors.$inferSelect;
export type InsertCustomerBudget = z.infer<typeof insertCustomerBudgetSchema>;
export type CustomerBudget = typeof customerBudgets.$inferSelect;
export type InsertCustomerBudgetLineItem = z.infer<typeof insertCustomerBudgetLineItemSchema>;
export type CustomerBudgetLineItem = typeof customerBudgetLineItems.$inferSelect;
export type InsertActualCostEntry = z.infer<typeof insertActualCostEntrySchema>;
export type ActualCostEntry = typeof actualCostEntries.$inferSelect;

export const insertBudgetHistorySchema = createInsertSchema(budgetHistory).omit({
  id: true,
  createdAt: true,
});

export type InsertBudgetHistory = z.infer<typeof insertBudgetHistorySchema>;
export type BudgetHistory = typeof budgetHistory.$inferSelect;

export const insertReportTemplateSchema = createInsertSchema(reportTemplates)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(200),
    description: xssSafeTextarea(1000).optional(),
  });

export type InsertReportTemplate = z.infer<typeof insertReportTemplateSchema>;
export type ReportTemplate = typeof reportTemplates.$inferSelect;

// Reporting Engine Zod validation schemas
export const reportFiltersSchema = z.object({
  dateRange: z.object({
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
  }),
  customerIds: z.array(z.number().int().positive()),
  reportAudience: z.enum(["internal", "customer-facing"]).optional(),
});

export const reportSectionConfigSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  enabled: z.boolean(),
  order: z.number().int().min(0),
  config: z.record(z.unknown()).optional(),
});

export const createReportTemplateSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  templateType: z.string().min(1),
  audience: z.enum(["internal", "customer-facing"]),
  sections: z.array(reportSectionConfigSchema).min(1),
});

// Legacy (template-driven) report generation schema. Retained for the legacy
// report-builder routes until they are retired in the reports redesign.
export const legacyGenerateReportSchema = z.object({
  templateId: z.number().int().positive(),
  filters: reportFiltersSchema,
  title: z.string().min(1).max(255).optional(),
});

// ── Reports redesign: canonical report generation/validation schemas ─────────
// Reusable date range with a startDate <= endDate refinement.
export const dateRangeSchema = z
  .object({
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
  })
  .refine((r) => new Date(r.startDate) <= new Date(r.endDate), {
    message: "startDate must be on or before endDate",
  });

// Generate a report from a predefined Report_Type. A non-empty reportTypeId is
// required so a missing/blank type is reported as a validation error (Req 2.5).
export const generateReportSchema = z.object({
  reportTypeId: z.string().min(1, "A report type must be selected"),
  customerId: z.number().int().positive().optional(),
  dateRange: dateRangeSchema,
  audience: z.enum(["internal", "customer-facing"]),
  title: z.string().min(1).max(255).optional(),
});

// Query parameters for the paginated report library listing. Values arrive as
// strings on the query string, so numeric fields are coerced with defaults.
export const listReportsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  customerId: z.coerce.number().int().positive().optional(),
  audience: z.enum(["internal", "customer-facing"]).optional(),
  type: z.string().optional(),
});

export const createReportScheduleSchema = z.object({
  templateId: z.number().int().positive(),
  name: z.string().min(1).max(255),
  cadence: z.enum(["weekly", "monthly", "quarterly"]),
  filters: reportFiltersSchema,
  recipients: z.array(z.string().email()).min(1).max(50),
  timezone: z.string().min(1).max(100),
});

export type ReportFilters = z.infer<typeof reportFiltersSchema>;
export type ReportSectionConfig = z.infer<typeof reportSectionConfigSchema>;
export type CreateReportTemplate = z.infer<typeof createReportTemplateSchema>;
export type LegacyGenerateReport = z.infer<typeof legacyGenerateReportSchema>;
export type GenerateReport = z.infer<typeof generateReportSchema>;
export type DateRange = z.infer<typeof dateRangeSchema>;
export type ListReportsQuery = z.infer<typeof listReportsQuerySchema>;
export type CreateReportSchedule = z.infer<typeof createReportScheduleSchema>;

export const insertGeneratedReportSchema = createInsertSchema(generatedReports)
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    title: xssSafeString(200),
  });

export type InsertGeneratedReport = z.infer<typeof insertGeneratedReportSchema>;
export type GeneratedReport = typeof generatedReports.$inferSelect;

export const insertReportScheduleSchema = createInsertSchema(reportSchedules)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(255),
  });

export type InsertReportSchedule = z.infer<typeof insertReportScheduleSchema>;
export type ReportSchedule = typeof reportSchedules.$inferSelect;

export const insertReportScheduleRunSchema = createInsertSchema(reportScheduleRuns)
  .omit({
    id: true,
    runAt: true,
  });

export type InsertReportScheduleRun = z.infer<typeof insertReportScheduleRunSchema>;
export type ReportScheduleRun = typeof reportScheduleRuns.$inferSelect;

export const insertBudgetTemplateSchema = createInsertSchema(budgetTemplates)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(200),
    description: xssSafeTextarea(1000).optional(),
  });

export type InsertBudgetTemplate = z.infer<typeof insertBudgetTemplateSchema>;
export type BudgetTemplate = typeof budgetTemplates.$inferSelect;

export const insertQbrRecommendationSchema = createInsertSchema(
  qbrRecommendations,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    budgetAllocated: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val !== undefined ? String(val) : undefined)),
    actualCost: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val !== undefined ? String(val) : undefined)),
    estimatedCost: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => (val !== undefined ? String(val) : undefined)),
  });

export type InsertQbrRecommendation = z.infer<
  typeof insertQbrRecommendationSchema
>;
export type QbrRecommendation = typeof qbrRecommendations.$inferSelect;
export type InsertFindingsLibrary = z.infer<typeof insertFindingsLibrarySchema>;
export type FindingsLibrary = typeof findingsLibrary.$inferSelect;
export type InsertQbrFindingsUsage = z.infer<
  typeof insertQbrFindingsUsageSchema
>;
export type QbrFindingsUsage = typeof qbrFindingsUsage.$inferSelect;

// Onboarding-related types
export type InsertOnboardingTour = z.infer<typeof insertOnboardingTourSchema>;
export type OnboardingTour = typeof onboardingTours.$inferSelect;
export type InsertTooltipStep = z.infer<typeof insertTooltipStepSchema>;
export type TooltipStep = typeof tooltipSteps.$inferSelect;
export type InsertUserOnboardingProgress = z.infer<
  typeof insertUserOnboardingProgressSchema
>;
export type UserOnboardingProgress = typeof userOnboardingProgress.$inferSelect;
export type InsertAssessmentReviewHistory = z.infer<
  typeof insertAssessmentReviewHistorySchema
>;
export type AssessmentReviewHistory = typeof assessmentReviewHistory.$inferSelect;
export type InsertAssessmentReviewCycle = z.infer<
  typeof insertAssessmentReviewCycleSchema
>;
export type AssessmentReviewCycle = typeof assessmentReviewCycles.$inferSelect;

// Organization-related types
export type InsertOrganization = z.infer<typeof insertOrganizationSchema>;
export type Organization = typeof organizations.$inferSelect;

// Standardized plan limits configuration
// NOTE: User limits have been removed — users are unlimited on all tiers.
export const PLAN_LIMITS = {
  Starter: {
    tier: "starter",
    maxCustomers: 20,
    maxAssessments: 250,
  },
  Professional: {
    tier: "professional",
    maxCustomers: 100,
    maxAssessments: 500,
  },
  Enterprise: {
    tier: "enterprise",
    maxCustomers: -1, // -1 means unlimited
    maxAssessments: 1000,
  },
} as const;

// Audit log table for tracking all user actions
export const auditLogs = pgTable(
  "audit_logs",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id),
    userId: varchar("user_id")
      .references(() => users.id),
    action: varchar("action").notNull(), // CREATE, UPDATE, DELETE, LOGIN, LOGOUT, AUTH_FAILURE, etc.
    resourceType: varchar("resource_type").notNull(), // user, customer, assessment, qbr, security, etc.
    resourceId: varchar("resource_id"), // ID of the affected resource
    resourceName: varchar("resource_name"), // Human-readable name of the resource
    details: jsonb("details"), // Additional context about the action (e.g., failure reasons for AUTH_FAILURE)
    ipAddress: varchar("ip_address"),
    userAgent: varchar("user_agent"),
    sessionId: varchar("session_id"),
    timestamp: timestamp("timestamp").defaultNow().notNull(),
    severity: varchar("severity").default("info"), // info, warning, error, critical
    category: varchar("category").default("user_action"), // user_action, system, security, compliance
  },
  (table) => [
    index("idx_audit_logs_organization").on(table.organizationId),
    index("idx_audit_logs_user").on(table.userId),
    index("idx_audit_logs_timestamp").on(table.timestamp),
    index("idx_audit_logs_action").on(table.action),
    index("idx_audit_logs_resource").on(table.resourceType, table.resourceId),
  ],
);

// Audit log schema and types
export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({
  id: true,
  timestamp: true,
});

export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type AuditLog = typeof auditLogs.$inferSelect;

// AI Usage Logs table for tracking token usage per organization
export const aiUsageLogs = pgTable(
  "ai_usage_logs",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    userId: varchar("user_id").references(() => users.id),
    
    // Request details
    operation: varchar("operation").notNull(), // 'qbr_generation', 'summary_regeneration', 'assessment_summary', etc.
    model: varchar("model").notNull(), // 'gpt-4', 'claude-3-sonnet', etc.
    
    // Token usage
    promptTokens: integer("prompt_tokens").notNull().default(0),
    completionTokens: integer("completion_tokens").notNull().default(0),
    totalTokens: integer("total_tokens").notNull().default(0),
    
    // Context
    resourceType: varchar("resource_type"), // 'qbr', 'assessment', etc.
    resourceId: integer("resource_id"), // ID of the QBR, assessment, etc.
    metadata: jsonb("metadata"), // Additional context (tone, customer_id, etc.)
    
    // Timing
    durationMs: integer("duration_ms"), // How long the API call took
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_ai_usage_organization").on(table.organizationId),
    index("idx_ai_usage_created_at").on(table.createdAt),
    index("idx_ai_usage_operation").on(table.operation),
    index("idx_ai_usage_org_date").on(table.organizationId, table.createdAt),
  ],
);

export const insertAiUsageLogSchema = createInsertSchema(aiUsageLogs).omit({
  id: true,
  createdAt: true,
});

export type InsertAiUsageLog = z.infer<typeof insertAiUsageLogSchema>;
export type AiUsageLog = typeof aiUsageLogs.$inferSelect;

// Webhook Events table for idempotency protection
export const webhookEvents = pgTable(
  "webhook_events",
  {
    id: serial("id").primaryKey(),
    eventId: varchar("event_id").notNull().unique(), // Paddle's event_id from webhook payload
    eventType: varchar("event_type").notNull(), // subscription.created, subscription.updated, etc.
    status: varchar("status").notNull().default("processing"), // processing, completed, failed
    payload: jsonb("payload").notNull(), // Full webhook payload for debugging
    processedAt: timestamp("processed_at"), // When processing completed
    error: text("error"), // Error message if processing failed
    retryCount: integer("retry_count").default(0), // Number of retry attempts
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("idx_webhook_events_event_id").on(table.eventId),
    index("idx_webhook_events_event_type").on(table.eventType),
    index("idx_webhook_events_status").on(table.status),
    index("idx_webhook_events_created_at").on(table.createdAt),
  ],
);

// Webhook Events schema and types
export const insertWebhookEventSchema = createInsertSchema(webhookEvents).omit({
  id: true,
  createdAt: true,
});

export type InsertWebhookEvent = z.infer<typeof insertWebhookEventSchema>;
export type WebhookEvent = typeof webhookEvents.$inferSelect;

// Enhanced user type with organization context
export type UserWithOrganization = User & {
  organization?: Organization;
};

// Admin dashboard schemas and types
export const insertUserLoginHistorySchema = createInsertSchema(
  userLoginHistory,
).omit({
  id: true,
  createdAt: true,
});

export type InsertUserLoginHistory = z.infer<
  typeof insertUserLoginHistorySchema
>;
export type UserLoginHistory = typeof userLoginHistory.$inferSelect;

// Remembered ("trusted") devices for login notifications.
// A browser that has been seen before stores an opaque random token in an
// httpOnly cookie; only the sha256 hash of that token is persisted here so a
// returning browser can be recognized without re-notifying the user.
export const trustedDevices = pgTable(
  "trusted_devices",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    organizationId: uuid("organization_id").references(() => organizations.id, {
      onDelete: "cascade",
    }),
    tokenHash: varchar("token_hash", { length: 64 }).notNull(),
    deviceFingerprint: varchar("device_fingerprint"),
    lastIpAddress: varchar("last_ip_address"),
    lastIpPrefix: varchar("last_ip_prefix"),
    lastUserAgent: text("last_user_agent"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    lastSeenAt: timestamp("last_seen_at").defaultNow().notNull(),
    expiresAt: timestamp("expires_at").notNull(),
  },
  (table) => [
    index("idx_trusted_devices_user").on(table.userId),
    index("idx_trusted_devices_expires_at").on(table.expiresAt),
    uniqueIndex("uq_trusted_devices_user_token").on(
      table.userId,
      table.tokenHash,
    ),
  ],
);

export const insertTrustedDeviceSchema = createInsertSchema(trustedDevices).omit(
  {
    id: true,
    createdAt: true,
  },
);

export type InsertTrustedDevice = z.infer<typeof insertTrustedDeviceSchema>;
export type TrustedDevice = typeof trustedDevices.$inferSelect;

// Platform feedback table
export const platformFeedback = pgTable(
  "platform_feedback",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    userId: varchar("user_id")
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    category: varchar("category").notNull(), // bug, feature_request, improvement, general
    title: varchar("title").notNull(),
    description: text("description").notNull(),
    priority: varchar("priority").default("medium"), // low, medium, high, critical
    status: varchar("status").default("submitted"), // submitted, in_review, in_progress, resolved, closed
    userAgent: text("user_agent"), // Browser/device info for bug reports
    currentPage: varchar("current_page"), // Page where feedback was submitted
    attachments: jsonb("attachments"), // Array of attachment URLs/metadata
    adminNotes: text("admin_notes"), // Internal notes from admin/support team
    trelloCardId: varchar("trello_card_id"), // ID of created Trello card
    trelloBoardId: varchar("trello_board_id"), // Trello board where card was created
    emailSent: boolean("email_sent").default(false), // Whether email was sent to Trello
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_platform_feedback_organization").on(table.organizationId),
    index("idx_platform_feedback_user").on(table.userId),
    index("idx_platform_feedback_status").on(table.status),
    index("idx_platform_feedback_category").on(table.category),
  ],
);

export const insertPlatformFeedbackSchema = createInsertSchema(platformFeedback).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  emailSent: true,
  trelloCardId: true,
  trelloBoardId: true,
});

export type InsertPlatformFeedback = z.infer<typeof insertPlatformFeedbackSchema>;
export type PlatformFeedback = typeof platformFeedback.$inferSelect;

// Tech Stack Audit Feature Tables
// Technology categories that MSPs can define for their standards
export const techStackCategories = pgTable(
  "tech_stack_categories",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    name: varchar("name").notNull(), // e.g., "Operating Systems", "Backup Solutions", "Security Tools"
    description: text("description"),
    displayOrder: integer("display_order").default(0), // For custom ordering in UI
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_tech_stack_categories_organization").on(table.organizationId),
  ],
);

// Organization's technology standards - approved/required technologies for each category
export const techStackStandards = pgTable(
  "tech_stack_standards",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    categoryId: integer("category_id")
      .references(() => techStackCategories.id, { onDelete: "cascade" })
      .notNull(),
    name: varchar("name").notNull(), // e.g., "Windows Server 2022", "Veeam Backup & Replication"
    version: varchar("version"), // e.g., "2022", "12.0", "latest"
    vendor: varchar("vendor"), // e.g., "Microsoft", "Veeam"
    complianceStatus: varchar("compliance_status").notNull().default("approved"), // approved, recommended, deprecated, prohibited
    description: text("description"),
    notes: text("notes"), // Why this is approved/deprecated/etc.
    isRequired: boolean("is_required").default(false), // Must have vs. should have
    // Recommendation-shaped metadata (tech-stack-gap-recommendations spec, Req 3.1).
    // Additive + defaulted/nullable so existing standards remain valid. A promoted
    // gap inherits this real content instead of AI-generated text.
    estimatedCost: decimal("estimated_cost", { precision: 12, scale: 2 }), // USD, 0.00–999,999,999.99 (Zod-enforced)
    estimatedEffort: integer("estimated_effort"), // hours, 0–100,000 (Zod-enforced)
    businessJustification: text("business_justification"), // 0–5,000 chars (Zod-enforced)
    defaultPriority: varchar("default_priority").default("medium"), // low | medium | high
    // Lifecycle / grandfathering columns (standard-change-propagation spec, Req 1, 6).
    // All additive + nullable so existing standards stay valid with no backfill (Req 8.9).
    effectiveDate: timestamp("effective_date"), // current version's effective date
    sunsetDate: timestamp("sunset_date"), // optional sunset for a tightening change
    // Self-referential swap target for a `replaced` change. Lazy AnyPgColumn
    // forward-reference form because the table references itself.
    replacedByStandardId: integer("replaced_by_standard_id").references(
      (): AnyPgColumn => techStackStandards.id,
      { onDelete: "set null" },
    ),
    transitionWindowDays: integer("transition_window_days"), // per-standard window (0–3,650, Zod-enforced)
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_tech_stack_standards_organization").on(table.organizationId),
    index("idx_tech_stack_standards_category").on(table.categoryId),
    index("idx_tech_stack_standards_compliance").on(table.complianceStatus),
  ],
);

// Append-only version history for techStackStandards (standard-change-propagation
// spec, Requirement 1). Each row is an immutable snapshot of every versioned
// attribute as it stood over an effective interval. No `updatedAt`: rows are never
// mutated or deleted by application code (append-only, Req 1.3).
export const techStackStandardVersions = pgTable(
  "tech_stack_standard_versions",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    standardId: integer("standard_id")
      .references(() => techStackStandards.id, { onDelete: "cascade" })
      .notNull(),
    versionSeq: integer("version_seq").notNull(), // 1..N+1, contiguous (Req 1.7)
    effectiveFrom: timestamp("effective_from").notNull(), // interval start (Req 1.4)
    // --- snapshot of every versioned attribute (Req 1.1, 1.2, 1.6) ---
    name: varchar("name").notNull(),
    vendor: varchar("vendor"),
    version: varchar("version"),
    complianceStatus: varchar("compliance_status").notNull(),
    isRequired: boolean("is_required").notNull().default(false),
    estimatedCost: decimal("estimated_cost", { precision: 12, scale: 2 }),
    estimatedEffort: integer("estimated_effort"),
    businessJustification: text("business_justification"),
    defaultPriority: varchar("default_priority"),
    effectiveDate: timestamp("effective_date"),
    sunsetDate: timestamp("sunset_date"),
    replacedByStandardId: integer("replaced_by_standard_id"),
    createdAt: timestamp("created_at").defaultNow(), // append-only; no updatedAt
  },
  (t) => [
    index("idx_tssv_org").on(t.organizationId),
    index("idx_tssv_standard").on(t.standardId),
    // exactly one row per (standard, versionSeq); enforces contiguity at the app layer
    uniqueIndex("uq_tssv_standard_seq").on(t.standardId, t.versionSeq),
    index("idx_tssv_standard_effective").on(t.standardId, t.effectiveFrom),
  ],
);

// Append-only, classified change-event log for standard changes
// (standard-change-propagation spec, Requirement 2). One immutable row per
// attribute-altering change. No `updatedAt`: rows are never mutated or deleted.
export const techStackChangeEvents = pgTable(
  "tech_stack_change_events",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    standardId: integer("standard_id")
      .references(() => techStackStandards.id, { onDelete: "cascade" })
      .notNull(),
    versionId: integer("version_id") // the version this change produced
      .references(() => techStackStandardVersions.id, { onDelete: "cascade" })
      .notNull(),
    changeType: varchar("change_type").notNull(), // added|deprecated|prohibited|replaced|modified (Req 2.2)
    attributeDiffs: jsonb("attribute_diffs").notNull(), // [{ attribute, prior, next }] (Req 2.1)
    replacedByStandardId: integer("replaced_by_standard_id").references(
      (): AnyPgColumn => techStackStandards.id,
      { onDelete: "set null" },
    ), // (Req 2.4)
    actorId: varchar("actor_id")
      .references(() => users.id)
      .notNull(),
    effectiveDate: timestamp("effective_date").notNull(),
    sunsetDate: timestamp("sunset_date"), // optional grandfathering (Req 6.1)
    transitionWindowDays: integer("transition_window_days"), // optional grandfathering (Req 6.1)
    committedAt: timestamp("committed_at").defaultNow().notNull(), // UTC commit instant (Req 2.1)
  },
  (t) => [
    index("idx_tsce_org").on(t.organizationId),
    index("idx_tsce_standard").on(t.standardId),
    // Change type is constrained to exactly one of the five classified values (Req 2.2).
    check(
      "chk_tsce_change_type",
      sql`${t.changeType} IN ('added', 'deprecated', 'prohibited', 'replaced', 'modified')`,
    ),
  ],
);

// Customer's actual technology stack
export const customerTechStack = pgTable(
  "customer_tech_stack",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    categoryId: integer("category_id")
      .references(() => techStackCategories.id, { onDelete: "cascade" })
      .notNull(),
    name: varchar("name").notNull(), // Technology name as deployed at customer
    version: varchar("version"),
    vendor: varchar("vendor"),
    quantity: integer("quantity").default(1), // Number of licenses/instances
    location: varchar("location"), // Server room, cloud, etc.
    lastVerified: timestamp("last_verified"), // When this was last confirmed
    notes: text("notes"),
    // Inventory auto-population provenance / lifecycle.
    // Additive + defaulted so existing/manual rows behave as before.
    source: varchar("source").default("manual"), // manual | discovered
    status: varchar("status").default("confirmed"), // proposed | confirmed | rejected | stale
    discoveredFromObservationId: integer("discovered_from_observation_id").references(
      () => customerObservations.id,
      { onDelete: "set null" },
    ), // provenance link to the source inventory observation
    lastDetectedAt: timestamp("last_detected_at"), // last time an observation reported this item
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_customer_tech_stack_organization").on(table.organizationId),
    index("idx_customer_tech_stack_customer").on(table.customerId),
    index("idx_customer_tech_stack_category").on(table.categoryId),
  ],
);

// Audit sessions - when an MSP conducts a compliance check
export const techStackAudits = pgTable(
  "tech_stack_audits",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    conductedById: varchar("conducted_by_id")
      .references(() => users.id)
      .notNull(),
    auditDate: timestamp("audit_date").notNull().defaultNow(),
    status: varchar("status").notNull().default("in_progress"), // in_progress, completed, archived
    overallComplianceScore: decimal("overall_compliance_score", {
      precision: 5,
      scale: 2,
    }), // Percentage 0-100
    title: varchar("title").notNull(),
    summary: text("summary"), // Executive summary of findings
    recommendations: text("recommendations"), // High-level recommendations
    nextAuditDate: timestamp("next_audit_date"), // Recommended next audit
    // Links a gap-analysis run to the review-cycle period that triggered it
    // (tech-stack-gap-recommendations spec, Req 1.2). Nullable + additive so
    // existing audit rows remain valid.
    reviewCycleId: integer("review_cycle_id").references(
      () => assessmentReviewCycles.id,
      { onDelete: "set null" },
    ),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_tech_stack_audits_organization").on(table.organizationId),
    index("idx_tech_stack_audits_customer").on(table.customerId),
    index("idx_tech_stack_audits_conducted_by").on(table.conductedById),
    index("idx_tech_stack_audits_status").on(table.status),
  ],
);

// Individual findings from audits - specific compliance issues or successes
export const techStackAuditFindings = pgTable(
  "tech_stack_audit_findings",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    auditId: integer("audit_id")
      .references(() => techStackAudits.id, { onDelete: "cascade" })
      .notNull(),
    categoryId: integer("category_id")
      .references(() => techStackCategories.id)
      .notNull(),
    customerTechId: integer("customer_tech_id")
      .references(() => customerTechStack.id, { onDelete: "set null" }),
    standardId: integer("standard_id")
      .references(() => techStackStandards.id, { onDelete: "set null" }),
    findingType: varchar("finding_type").notNull(), // compliant, non_compliant, missing, deprecated, unauthorized
    severity: varchar("severity").notNull().default("medium"), // low, medium, high, critical
    title: varchar("title").notNull(),
    description: text("description").notNull(),
    recommendation: text("recommendation"), // Specific remediation advice
    estimatedCost: decimal("estimated_cost", { precision: 12, scale: 2 }), // Cost to remediate
    estimatedEffort: varchar("estimated_effort"), // e.g., "2 hours", "1 week"
    priority: varchar("priority").default("medium"), // low, medium, high, critical
    status: varchar("status").default("open"), // open, in_progress, resolved, accepted_risk, false_positive
    resolvedAt: timestamp("resolved_at"),
    resolvedBy: varchar("resolved_by").references(() => users.id),
    resolutionNotes: text("resolution_notes"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_tech_stack_audit_findings_organization").on(table.organizationId),
    index("idx_tech_stack_audit_findings_audit").on(table.auditId),
    index("idx_tech_stack_audit_findings_category").on(table.categoryId),
    index("idx_tech_stack_audit_findings_type").on(table.findingType),
    index("idx_tech_stack_audit_findings_severity").on(table.severity),
    index("idx_tech_stack_audit_findings_status").on(table.status),
  ],
);

// Relations for Tech Stack Audit tables
export const techStackCategoriesRelations = relations(
  techStackCategories,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [techStackCategories.organizationId],
      references: [organizations.id],
    }),
    standards: many(techStackStandards),
    customerTech: many(customerTechStack),
    findings: many(techStackAuditFindings),
  }),
);

export const techStackStandardsRelations = relations(
  techStackStandards,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [techStackStandards.organizationId],
      references: [organizations.id],
    }),
    category: one(techStackCategories, {
      fields: [techStackStandards.categoryId],
      references: [techStackCategories.id],
    }),
    findings: many(techStackAuditFindings),
  }),
);

export const customerTechStackRelations = relations(
  customerTechStack,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [customerTechStack.organizationId],
      references: [organizations.id],
    }),
    customer: one(customers, {
      fields: [customerTechStack.customerId],
      references: [customers.id],
    }),
    category: one(techStackCategories, {
      fields: [customerTechStack.categoryId],
      references: [techStackCategories.id],
    }),
    discoveredFromObservation: one(customerObservations, {
      fields: [customerTechStack.discoveredFromObservationId],
      references: [customerObservations.id],
    }),
    findings: many(techStackAuditFindings),
  }),
);

export const techStackAuditsRelations = relations(
  techStackAudits,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [techStackAudits.organizationId],
      references: [organizations.id],
    }),
    customer: one(customers, {
      fields: [techStackAudits.customerId],
      references: [customers.id],
    }),
    conductedBy: one(users, {
      fields: [techStackAudits.conductedById],
      references: [users.id],
    }),
    findings: many(techStackAuditFindings),
  }),
);

export const techStackAuditFindingsRelations = relations(
  techStackAuditFindings,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [techStackAuditFindings.organizationId],
      references: [organizations.id],
    }),
    audit: one(techStackAudits, {
      fields: [techStackAuditFindings.auditId],
      references: [techStackAudits.id],
    }),
    category: one(techStackCategories, {
      fields: [techStackAuditFindings.categoryId],
      references: [techStackCategories.id],
    }),
    customerTech: one(customerTechStack, {
      fields: [techStackAuditFindings.customerTechId],
      references: [customerTechStack.id],
    }),
    standard: one(techStackStandards, {
      fields: [techStackAuditFindings.standardId],
      references: [techStackStandards.id],
    }),
    resolver: one(users, {
      fields: [techStackAuditFindings.resolvedBy],
      references: [users.id],
    }),
  }),
);

// Insert schemas for Tech Stack Audit tables
export const insertTechStackCategorySchema = createInsertSchema(
  techStackCategories,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(200),
    description: xssSafeTextarea(1000).optional(),
  });

export const insertTechStackStandardSchema = createInsertSchema(
  techStackStandards,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(200),
    version: z.preprocess(
      (val) => (val === "" ? undefined : val),
      xssSafeString(50).optional()
    ),
    vendor: z.preprocess(
      (val) => (val === "" ? undefined : val),
      xssSafeString(100).optional()
    ),
    description: xssSafeTextarea(1000).optional(),
    notes: xssSafeTextarea(1000).optional(),
  });

export const insertCustomerTechStackSchema = createInsertSchema(
  customerTechStack,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(200),
    version: xssSafeString(50).optional(),
    vendor: xssSafeString(100).optional(),
    location: xssSafeString(200).optional(),
    notes: xssSafeTextarea(1000).optional(),
    source: z.enum(["manual", "discovered"]).default("manual"),
    status: z
      .enum(["proposed", "confirmed", "rejected", "stale"])
      .default("confirmed"),
  });

export const insertTechStackAuditSchema = createInsertSchema(techStackAudits)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    title: xssSafeString(200),
    summary: xssSafeTextarea(2000).optional(),
    recommendations: xssSafeTextarea(2000).optional(),
  });

export const insertTechStackAuditFindingSchema = createInsertSchema(
  techStackAuditFindings,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    title: xssSafeString(200),
    description: xssSafeTextarea(2000),
    recommendation: xssSafeTextarea(2000).optional(),
    estimatedEffort: xssSafeString(100).optional(),
    resolutionNotes: xssSafeTextarea(1000).optional(),
  });

// ---------------------------------------------------------------------------
// Tech-Stack Gap → Recommendation: request-body Zod validation schemas
// ---------------------------------------------------------------------------
// These validate the state-changing gap endpoints (recommendation metadata,
// gap analysis runs, urgency overrides, accept-risk). Constraints mirror the
// additive schema columns and the urgency/lifecycle vocabulary so the engine
// always receives well-formed, in-range input. (Requirements 3.2, 5.1, 5.7,
// 6.3, 9.6)

// Sets the recommendation-shaped metadata on a techStackStandards row.
// estimatedCost: USD decimal 0.00–999,999,999.99; estimatedEffort: integer
// hours 0–100,000; businessJustification: 0–5,000 chars; defaultPriority enum.
// All fields are optional so a vCIO may set them independently; defaultPriority
// defaults to "medium" to match the column default. (Requirement 3.2)
export const recommendationMetadataSchema = z.object({
  estimatedCost: z
    .number()
    .min(0, "Estimated cost cannot be negative")
    .max(999999999.99, "Estimated cost exceeds maximum of 999,999,999.99")
    .optional(),
  estimatedEffort: z
    .number()
    .int("Estimated effort must be a whole number of hours")
    .min(0, "Estimated effort cannot be negative")
    .max(100000, "Estimated effort exceeds maximum of 100,000 hours")
    .optional(),
  businessJustification: xssSafeTextarea(5000).optional(),
  defaultPriority: z.enum(["low", "medium", "high"]).default("medium"),
});

// Runs gap analysis for a customer within a specific review-cycle period.
// (Requirement 1, gating/validation per 9.6)
export const runGapAnalysisSchema = z.object({
  reviewCycleId: z.number().int().positive(),
});

// vCIO override of a gap's default urgency: priority enum + timing enum.
// (Requirements 5.1, 5.7)
export const urgencyOverrideSchema = z.object({
  priority: z.enum(["low", "medium", "high"]),
  timing: z.enum(["near_term", "roadmap"]),
});

// Accept-risk decision on a gap; reason is mandatory and bounded to 1–2,000
// characters (Requirements 6.3, 6.4).
export const acceptRiskSchema = z.object({
  reason: xssSafeTextarea(2000).refine((val) => val.length >= 1, {
    message: "A reason is required",
  }),
});

export type RecommendationMetadataInput = z.infer<
  typeof recommendationMetadataSchema
>;
export type RunGapAnalysisInput = z.infer<typeof runGapAnalysisSchema>;
export type UrgencyOverrideInput = z.infer<typeof urgencyOverrideSchema>;
export type AcceptRiskInput = z.infer<typeof acceptRiskSchema>;

// ---------------------------------------------------------------------------
// Unified Review Cycle Evaluation: request-body Zod validation schemas
// ---------------------------------------------------------------------------
// Spec: .kiro/specs/unified-review-cycle-evaluation
// These validate the state-changing unified-review endpoints. The review-cycle
// id always comes from the route param and is validated/scoped server-side
// against the authenticated user's organization (never trusted from the body).
// Both bodies carry only optional, additive notes — completion is gated on the
// service's computed lens state, never on a client-supplied completion flag, and
// no gap status vocabulary is introduced (gap transitions reuse GAP_STATUSES /
// isAllowedGapTransition from shared/gapLifecycle.ts). (Requirements 5.4, 7.3, 8.6)

// POST /api/review-cycles/:id/unified-review — run a unified review pass.
// Only an optional notes/summary field; the cycle id is a route param (8.6).
export const runUnifiedPassSchema = z.object({
  unifiedReviewNotes: xssSafeTextarea(2000).optional(),
});

// POST /api/review-cycles/:id/complete — mark the cycle unified-complete.
// Completion is gated on computed lens state, never on a client-supplied
// completion flag; only an optional notes/summary field is accepted (5.4, 8.6).
export const completeUnifiedSchema = z.object({
  unifiedReviewNotes: xssSafeTextarea(2000).optional(),
});

export type RunUnifiedPassInput = z.infer<typeof runUnifiedPassSchema>;
export type CompleteUnifiedInput = z.infer<typeof completeUnifiedSchema>;

// ---------------------------------------------------------------------------
// Standard Change Propagation: request-body Zod validation schemas
// ---------------------------------------------------------------------------
// Validate the state-changing standard-change endpoints (record a classified
// change, configure an org/per-change transition window) and the as-of query.
// Constraints mirror the additive lifecycle columns and the versioned-attribute
// set (name, vendor, version, complianceStatus, isRequired, estimatedCost,
// estimatedEffort, businessJustification, defaultPriority, effectiveDate,
// sunsetDate, replacedByStandardId) so the engine always receives well-formed,
// in-range input. (standard-change-propagation spec, Requirements 4.3, 6.1, 8.6)

// Gap provenance is constrained to exactly { drift, standard_change } — mirrors
// the DB CHECK constraint on customerRisks.provenance. (Requirements 4.3, 4.4)
export const provenanceSchema = z.enum(["drift", "standard_change"]);

// A transition window expressed as a non-negative integer number of days,
// 0–3,650. Used for the org default and the per-change window. (Req 6.1, 6.2)
export const transitionWindowSchema = z.object({
  windowDays: z
    .number()
    .int("Transition window must be a whole number of days")
    .min(0, "Transition window cannot be negative")
    .max(3650, "Transition window exceeds maximum of 3,650 days"),
});

// As-of reconstruction query: a single date, coerced from an ISO string. (Req 1.4)
export const asOfQuerySchema = z.object({
  date: z.coerce.date(),
});

// Records a classified standard change: the complete post-change set of
// versioned attributes plus an optional, mutually-exclusive transition window
// expressed as EITHER windowDays (0–3,650) OR an explicit sunsetDate on/after
// the effective date. effectiveDate is required. (Requirements 6.1, 8.6)
export const recordStandardChangeSchema = z
  .object({
    // --- post-change versioned attributes ---
    name: xssSafeString(200),
    vendor: z.preprocess(
      (val) => (val === "" ? undefined : val),
      xssSafeString(100).optional().nullable(),
    ),
    version: z.preprocess(
      (val) => (val === "" ? undefined : val),
      xssSafeString(50).optional().nullable(),
    ),
    complianceStatus: z.enum([
      "approved",
      "recommended",
      "deprecated",
      "prohibited",
    ]),
    isRequired: z.boolean().default(false),
    estimatedCost: z
      .number()
      .min(0, "Estimated cost cannot be negative")
      .max(999999999.99, "Estimated cost exceeds maximum of 999,999,999.99")
      .optional()
      .nullable(),
    estimatedEffort: z
      .number()
      .int("Estimated effort must be a whole number of hours")
      .min(0, "Estimated effort cannot be negative")
      .max(100000, "Estimated effort exceeds maximum of 100,000 hours")
      .optional()
      .nullable(),
    businessJustification: xssSafeTextarea(5000).optional().nullable(),
    defaultPriority: z.enum(["low", "medium", "high"]).optional(),
    effectiveDate: z.coerce.date(),
    replacedByStandardId: z.number().int().positive().optional().nullable(),
    // --- optional, mutually-exclusive transition window (Req 6.1) ---
    windowDays: z
      .number()
      .int("Transition window must be a whole number of days")
      .min(0, "Transition window cannot be negative")
      .max(3650, "Transition window exceeds maximum of 3,650 days")
      .optional(),
    sunsetDate: z.coerce.date().optional(),
  })
  .refine(
    (data) => !(data.windowDays !== undefined && data.sunsetDate !== undefined),
    {
      message: "Provide either windowDays or sunsetDate, not both",
      path: ["windowDays"],
    },
  )
  .refine(
    (data) =>
      data.sunsetDate === undefined || data.sunsetDate >= data.effectiveDate,
    {
      message: "sunsetDate must be on or after the effective date",
      path: ["sunsetDate"],
    },
  );

// Insert schemas derived from the two new append-only tables. Both omit the
// serial id and the DB-managed timestamps (createdAt / committedAt). These
// tables are append-only (Req 1.3): no update/delete schemas are derived.
export const insertTechStackStandardVersionSchema = createInsertSchema(
  techStackStandardVersions,
).omit({ id: true, createdAt: true });

export const insertTechStackChangeEventSchema = createInsertSchema(
  techStackChangeEvents,
).omit({ id: true, committedAt: true });

export type Provenance = z.infer<typeof provenanceSchema>;
export type TransitionWindowInput = z.infer<typeof transitionWindowSchema>;
export type AsOfQueryInput = z.infer<typeof asOfQuerySchema>;
export type RecordStandardChangeInput = z.infer<
  typeof recordStandardChangeSchema
>;
export type InsertTechStackStandardVersion = z.infer<
  typeof insertTechStackStandardVersionSchema
>;
export type TechStackStandardVersion =
  typeof techStackStandardVersions.$inferSelect;
export type InsertTechStackChangeEvent = z.infer<
  typeof insertTechStackChangeEventSchema
>;
export type TechStackChangeEvent = typeof techStackChangeEvents.$inferSelect;

// Types for Tech Stack Audit tables
export type InsertTechStackCategory = z.infer<
  typeof insertTechStackCategorySchema
>;
export type TechStackCategory = typeof techStackCategories.$inferSelect;
export type InsertTechStackStandard = z.infer<
  typeof insertTechStackStandardSchema
>;
export type TechStackStandard = typeof techStackStandards.$inferSelect;
export type InsertCustomerTechStack = z.infer<
  typeof insertCustomerTechStackSchema
>;
export type CustomerTechStack = typeof customerTechStack.$inferSelect;
export type InsertTechStackAudit = z.infer<typeof insertTechStackAuditSchema>;
export type TechStackAudit = typeof techStackAudits.$inferSelect;
export type InsertTechStackAuditFinding = z.infer<
  typeof insertTechStackAuditFindingSchema
>;
export type TechStackAuditFinding = typeof techStackAuditFindings.$inferSelect;

// ============================================================================
// INTEGRATION PROVIDER ABSTRACTION TABLES
// (spec: integration-provider-abstraction)
// ============================================================================

// Normalized, provider-agnostic observations emitted by integration providers.
// Downstream consumers (inventory auto-population, gap analysis, intake) read this
// store and never branch on provider. Idempotent on
// (organizationId, sourceProvider, sourceExternalId).
export const customerObservations = pgTable(
  "customer_observations",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    kind: varchar("kind").notNull(), // inventory_item | metric
    // inventory_item fields
    categoryHint: varchar("category_hint"), // provider's raw category label (pre-mapping)
    categoryId: integer("category_id").references(() => techStackCategories.id, {
      onDelete: "set null",
    }), // resolved via category mapping; null when unmapped
    name: varchar("name"),
    vendor: varchar("vendor"),
    version: varchar("version"),
    quantity: integer("quantity"),
    // metric fields
    metricKey: varchar("metric_key"), // e.g. rmm_patch_compliance
    value: decimal("value", { precision: 12, scale: 2 }),
    // provenance / idempotency
    sourceType: varchar("source_type").notNull(), // rmm | m365 | backup | psa | ...
    sourceProvider: varchar("source_provider").notNull(), // ninjaone | ncentral | ...
    sourceExternalId: varchar("source_external_id").notNull(),
    observedAt: timestamp("observed_at").notNull(),
    raw: jsonb("raw"), // original payload, debugging only
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_customer_observations_org_customer").on(
      table.organizationId,
      table.customerId,
    ),
    index("idx_customer_observations_org_kind").on(
      table.organizationId,
      table.kind,
    ),
    // Idempotency key: re-collection reconciles rather than duplicating.
    uniqueIndex("uq_customer_observations_source").on(
      table.organizationId,
      table.sourceProvider,
      table.sourceExternalId,
    ),
  ],
);

// Tenant-scoped mapping from a provider's product identifier to the MSP's tech
// stack category (and optionally a standard). Adding a provider's products =
// new mapping rows, not code.
export const techStackCategoryMappings = pgTable(
  "tech_stack_category_mappings",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    sourceProvider: varchar("source_provider").notNull(), // whose naming this maps
    productIdentifier: varchar("product_identifier").notNull(), // provider product name/key
    categoryId: integer("category_id")
      .references(() => techStackCategories.id, { onDelete: "cascade" })
      .notNull(),
    standardId: integer("standard_id").references(() => techStackStandards.id, {
      onDelete: "set null",
    }), // optional standard hint
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_tech_stack_category_mappings_org").on(table.organizationId),
    uniqueIndex("uq_tech_stack_category_mappings_lookup").on(
      table.organizationId,
      table.sourceProvider,
      table.productIdentifier,
    ),
  ],
);

export const customerObservationsRelations = relations(
  customerObservations,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [customerObservations.organizationId],
      references: [organizations.id],
    }),
    customer: one(customers, {
      fields: [customerObservations.customerId],
      references: [customers.id],
    }),
    category: one(techStackCategories, {
      fields: [customerObservations.categoryId],
      references: [techStackCategories.id],
    }),
    discoveredTechStack: many(customerTechStack),
  }),
);

export const techStackCategoryMappingsRelations = relations(
  techStackCategoryMappings,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [techStackCategoryMappings.organizationId],
      references: [organizations.id],
    }),
    category: one(techStackCategories, {
      fields: [techStackCategoryMappings.categoryId],
      references: [techStackCategories.id],
    }),
    standard: one(techStackStandards, {
      fields: [techStackCategoryMappings.standardId],
      references: [techStackStandards.id],
    }),
  }),
);

export const insertCustomerObservationSchema = createInsertSchema(
  customerObservations,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    categoryHint: xssSafeString(200).optional(),
    name: xssSafeString(300).optional(),
    vendor: xssSafeString(200).optional(),
    version: xssSafeString(100).optional(),
    metricKey: xssSafeString(100).optional(),
    sourceType: xssSafeString(50),
    sourceProvider: xssSafeString(50),
    sourceExternalId: xssSafeString(255),
  });

export const insertTechStackCategoryMappingSchema = createInsertSchema(
  techStackCategoryMappings,
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    sourceProvider: xssSafeString(50),
    productIdentifier: xssSafeString(300),
  });

export type CustomerObservation = typeof customerObservations.$inferSelect;
export type InsertCustomerObservation = z.infer<
  typeof insertCustomerObservationSchema
>;
export type TechStackCategoryMapping =
  typeof techStackCategoryMappings.$inferSelect;
export type InsertTechStackCategoryMapping = z.infer<
  typeof insertTechStackCategoryMappingSchema
>;


// ============================================================================
// ASSET LIFECYCLE MANAGEMENT TABLES
// ============================================================================

// Asset types and categories
export const ASSET_TYPES = [
  'workstation',
  'server',
  'network',
  'mobile',
  'peripheral',
  'software',
  'cloud',
  'other',
] as const;

export type AssetType = typeof ASSET_TYPES[number];

export const ASSET_CATEGORIES = {
  workstation: ['desktop', 'laptop', 'thin_client', 'all_in_one'],
  server: ['rack_server', 'tower_server', 'blade_server', 'virtual_machine', 'hypervisor'],
  network: ['switch', 'router', 'firewall', 'access_point', 'load_balancer', 'vpn_appliance'],
  mobile: ['smartphone', 'tablet', 'mobile_hotspot'],
  peripheral: ['printer', 'scanner', 'monitor', 'docking_station', 'ups', 'kvm'],
  software: ['operating_system', 'productivity', 'security', 'backup', 'database', 'custom'],
  cloud: ['saas', 'iaas', 'paas'],
} as const;

export const LIFECYCLE_STAGES = [
  'new',
  'current',
  'aging',
  'end_of_life',
  'retired',
] as const;

export type LifecycleStage = typeof LIFECYCLE_STAGES[number];

export const ASSET_STATUSES = [
  'active',
  'spare',
  'in_repair',
  'retired',
  'disposed',
  'lost',
] as const;

export type AssetStatus = typeof ASSET_STATUSES[number];

export const ASSET_CONDITIONS = [
  'excellent',
  'good',
  'fair',
  'poor',
] as const;

export type AssetCondition = typeof ASSET_CONDITIONS[number];

// Core assets table
export const assets = pgTable(
  "assets",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    
    // Asset identification
    assetTag: varchar("asset_tag", { length: 100 }),
    serialNumber: varchar("serial_number", { length: 255 }),
    name: varchar("name", { length: 255 }).notNull(),
    
    // Classification
    assetType: varchar("asset_type", { length: 50 }).notNull(),
    category: varchar("category", { length: 100 }),
    manufacturer: varchar("manufacturer", { length: 255 }),
    model: varchar("model", { length: 255 }),
    
    // Lifecycle dates
    purchaseDate: timestamp("purchase_date"),
    warrantyStartDate: timestamp("warranty_start_date"),
    warrantyEndDate: timestamp("warranty_end_date"),
    endOfLifeDate: timestamp("end_of_life_date"),
    endOfSupportDate: timestamp("end_of_support_date"),
    expectedReplacementDate: timestamp("expected_replacement_date"),
    
    // Financial
    purchaseCost: decimal("purchase_cost", { precision: 12, scale: 2 }),
    currentValue: decimal("current_value", { precision: 12, scale: 2 }),
    replacementCost: decimal("replacement_cost", { precision: 12, scale: 2 }),
    depreciationMethod: varchar("depreciation_method", { length: 50 }).default("straight_line"),
    usefulLifeMonths: integer("useful_life_months").default(60),
    
    // Status
    status: varchar("status", { length: 50 }).notNull().default("active"),
    lifecycleStage: varchar("lifecycle_stage", { length: 50 }).default("current"),
    condition: varchar("condition", { length: 50 }).default("good"),
    
    // Location & Assignment
    location: varchar("location", { length: 255 }),
    assignedTo: varchar("assigned_to", { length: 255 }),
    department: varchar("department", { length: 100 }),
    
    // Technical details
    specifications: jsonb("specifications").default({}),
    
    // Integration
    source: varchar("source", { length: 50 }).default("manual"),
    externalId: varchar("external_id", { length: 255 }),
    integrationId: integer("integration_id").references(() => integrations.id, { onDelete: "set null" }),
    lastSyncedAt: timestamp("last_synced_at"),
    
    // Audit
    notes: text("notes"),
    createdBy: varchar("created_by").references(() => users.id),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_assets_organization").on(table.organizationId),
    index("idx_assets_customer").on(table.customerId),
    index("idx_assets_type").on(table.assetType),
    index("idx_assets_warranty_end").on(table.warrantyEndDate),
    index("idx_assets_eol").on(table.endOfLifeDate),
    index("idx_assets_status").on(table.status),
    index("idx_assets_serial").on(table.serialNumber),
    index("idx_assets_lifecycle_stage").on(table.lifecycleStage),
    index("idx_assets_org_customer").on(table.organizationId, table.customerId),
  ],
);

// Warranty tracking table
export const assetWarranties = pgTable(
  "asset_warranties",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    assetId: integer("asset_id")
      .references(() => assets.id, { onDelete: "cascade" })
      .notNull(),
    
    // Warranty details
    warrantyType: varchar("warranty_type", { length: 100 }).default("standard"),
    provider: varchar("provider", { length: 255 }),
    contractNumber: varchar("contract_number", { length: 255 }),
    
    // Coverage
    startDate: timestamp("start_date").notNull(),
    endDate: timestamp("end_date").notNull(),
    coverageDescription: text("coverage_description"),
    
    // Status
    status: varchar("status", { length: 50 }).default("active"),
    
    // Vendor API data
    vendorWarrantyId: varchar("vendor_warranty_id", { length: 255 }),
    lastCheckedAt: timestamp("last_checked_at"),
    rawResponse: jsonb("raw_response"),
    
    // Cost
    annualCost: decimal("annual_cost", { precision: 12, scale: 2 }),
    
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_warranties_organization").on(table.organizationId),
    index("idx_warranties_asset").on(table.assetId),
    index("idx_warranties_end_date").on(table.endDate),
    index("idx_warranties_status").on(table.status),
  ],
);

// Asset Audit tables - individual asset compliance checks
export const assetAudits = pgTable(
  "asset_audits",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    assetId: integer("asset_id")
      .references(() => assets.id, { onDelete: "cascade" })
      .notNull(),
    
    // Audit status
    status: varchar("status", { length: 20 }).notNull().default("OK"), // OK, FAIL, WARNING
    auditType: varchar("audit_type", { length: 50 }).notNull().default("manual"), // manual, scheduled, automated
    
    // Failure details
    failureReason: text("failure_reason"), // Why the audit failed
    failureCategory: varchar("failure_category", { length: 100 }), // missing_warranty, expired_support, eol_hardware, missing_documentation, security_vulnerability
    severity: varchar("severity", { length: 20 }).default("medium"), // low, medium, high, critical
    
    // Audit metadata
    auditedBy: varchar("audited_by").references(() => users.id),
    auditedAt: timestamp("audited_at").defaultNow().notNull(),
    nextAuditDue: timestamp("next_audit_due"),
    
    // Remediation tracking
    remediationRequired: boolean("remediation_required").default(false),
    remediationNotes: text("remediation_notes"),
    remediationDueDate: timestamp("remediation_due_date"),
    
    // PSA ticket integration
    psaTicketId: varchar("psa_ticket_id", { length: 100 }), // External PSA ticket ID
    psaTicketUrl: text("psa_ticket_url"), // Link to PSA ticket
    psaTicketCreatedAt: timestamp("psa_ticket_created_at"),
    psaTicketStatus: varchar("psa_ticket_status", { length: 50 }), // open, in_progress, resolved, closed
    integrationId: integer("integration_id").references(() => integrations.id, { onDelete: "set null" }),
    
    // Resolution tracking
    resolvedAt: timestamp("resolved_at"),
    resolvedBy: varchar("resolved_by").references(() => users.id),
    resolutionNotes: text("resolution_notes"),
    
    // Audit details
    auditData: jsonb("audit_data"), // Detailed audit findings
    
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_tech_audits_organization").on(table.organizationId),
    index("idx_tech_audits_customer").on(table.customerId),
    index("idx_tech_audits_asset").on(table.assetId),
    index("idx_tech_audits_status").on(table.status),
    index("idx_tech_audits_severity").on(table.severity),
    index("idx_tech_audits_next_due").on(table.nextAuditDue),
    index("idx_tech_audits_psa_ticket").on(table.psaTicketId),
    index("idx_tech_audits_org_customer").on(table.organizationId, table.customerId),
  ],
);

// Tech Stack Audit Rules - configurable audit criteria
export const techStackAuditRules = pgTable(
  "tech_stack_audit_rules",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    category: varchar("category", { length: 100 }).notNull(), // warranty, support, eol, documentation, security
    
    // Rule configuration
    enabled: boolean("enabled").default(true),
    severity: varchar("severity", { length: 20 }).default("medium"), // low, medium, high, critical
    autoCreateTicket: boolean("auto_create_ticket").default(false), // Auto-create PSA ticket on failure
    
    // Rule criteria (JSON schema for flexible rule definitions)
    criteria: jsonb("criteria").notNull(), // { warrantyExpired: true, daysBeforeExpiry: 30, assetTypes: ['server', 'firewall'] }
    
    // PSA ticket template
    ticketTemplate: jsonb("ticket_template"), // { summary: "...", description: "...", priority: "high", boardId: 123 }
    
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_audit_rules_organization").on(table.organizationId),
    index("idx_audit_rules_enabled").on(table.enabled),
  ],
);

// Lifecycle events history
export const assetLifecycleEvents = pgTable(
  "asset_lifecycle_events",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    assetId: integer("asset_id")
      .references(() => assets.id, { onDelete: "cascade" })
      .notNull(),
    
    eventType: varchar("event_type", { length: 50 }).notNull(),
    eventDate: timestamp("event_date").notNull().defaultNow(),
    
    // Event details
    description: text("description"),
    performedBy: varchar("performed_by").references(() => users.id),
    cost: decimal("cost", { precision: 12, scale: 2 }),
    
    // Before/after for changes
    previousStatus: varchar("previous_status", { length: 50 }),
    newStatus: varchar("new_status", { length: 50 }),
    changes: jsonb("changes"),
    
    // Documentation
    notes: text("notes"),
    attachments: jsonb("attachments"),
    
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_lifecycle_events_organization").on(table.organizationId),
    index("idx_lifecycle_events_asset").on(table.assetId),
    index("idx_lifecycle_events_date").on(table.eventDate),
    index("idx_lifecycle_events_type").on(table.eventType),
  ],
);

// Asset alerts table
export const assetAlerts = pgTable(
  "asset_alerts",
  {
    id: serial("id").primaryKey(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    assetId: integer("asset_id")
      .references(() => assets.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    
    alertType: varchar("alert_type", { length: 50 }).notNull(),
    severity: varchar("severity", { length: 20 }).notNull().default("warning"),
    
    // Timing
    triggerDate: timestamp("trigger_date").notNull().defaultNow(),
    dueDate: timestamp("due_date"),
    daysUntilDue: integer("days_until_due"),
    
    // Status
    status: varchar("status", { length: 50 }).default("active"),
    acknowledgedBy: varchar("acknowledged_by").references(() => users.id),
    acknowledgedAt: timestamp("acknowledged_at"),
    resolvedAt: timestamp("resolved_at"),
    
    // Content
    title: varchar("title", { length: 255 }).notNull(),
    message: text("message").notNull(),
    recommendedAction: text("recommended_action"),
    
    // Linking
    qbrRecommendationId: integer("qbr_recommendation_id").references(() => qbrRecommendations.id, { onDelete: "set null" }),
    budgetLineItemId: integer("budget_line_item_id").references(() => customerBudgetLineItems.id, { onDelete: "set null" }),
    
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_asset_alerts_organization").on(table.organizationId),
    index("idx_asset_alerts_customer").on(table.customerId),
    index("idx_asset_alerts_asset").on(table.assetId),
    index("idx_asset_alerts_status").on(table.status),
    index("idx_asset_alerts_type").on(table.alertType),
    index("idx_asset_alerts_due_date").on(table.dueDate),
    index("idx_asset_alerts_severity").on(table.severity),
  ],
);

// Hardware catalog reference table (global)
export const hardwareCatalog = pgTable(
  "hardware_catalog",
  {
    id: serial("id").primaryKey(),
    
    // Identification
    manufacturer: varchar("manufacturer", { length: 255 }).notNull(),
    model: varchar("model", { length: 255 }).notNull(),
    modelNumber: varchar("model_number", { length: 255 }),
    
    // Classification
    assetType: varchar("asset_type", { length: 50 }).notNull(),
    category: varchar("category", { length: 100 }),
    
    // Lifecycle info
    releaseDate: timestamp("release_date"),
    endOfSaleDate: timestamp("end_of_sale_date"),
    endOfLifeDate: timestamp("end_of_life_date"),
    endOfSupportDate: timestamp("end_of_support_date"),
    
    // Specs template
    defaultSpecifications: jsonb("default_specifications").default({}),
    
    // Warranty info
    standardWarrantyMonths: integer("standard_warranty_months").default(36),
    
    // Pricing
    msrp: decimal("msrp", { precision: 12, scale: 2 }),
    typicalUsefulLifeMonths: integer("typical_useful_life_months").default(60),
    
    // Source
    source: varchar("source", { length: 50 }).default("manual"),
    lastUpdatedAt: timestamp("last_updated_at").defaultNow(),
    
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_catalog_manufacturer").on(table.manufacturer),
    index("idx_catalog_type").on(table.assetType),
    index("idx_catalog_eol").on(table.endOfLifeDate),
  ],
);

// Relations for Asset Lifecycle tables
export const assetsRelations = relations(assets, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [assets.organizationId],
    references: [organizations.id],
  }),
  customer: one(customers, {
    fields: [assets.customerId],
    references: [customers.id],
  }),
  integration: one(integrations, {
    fields: [assets.integrationId],
    references: [integrations.id],
  }),
  creator: one(users, {
    fields: [assets.createdBy],
    references: [users.id],
  }),
  warranties: many(assetWarranties),
  lifecycleEvents: many(assetLifecycleEvents),
  alerts: many(assetAlerts),
}));

export const assetWarrantiesRelations = relations(assetWarranties, ({ one }) => ({
  organization: one(organizations, {
    fields: [assetWarranties.organizationId],
    references: [organizations.id],
  }),
  asset: one(assets, {
    fields: [assetWarranties.assetId],
    references: [assets.id],
  }),
}));

export const assetLifecycleEventsRelations = relations(assetLifecycleEvents, ({ one }) => ({
  organization: one(organizations, {
    fields: [assetLifecycleEvents.organizationId],
    references: [organizations.id],
  }),
  asset: one(assets, {
    fields: [assetLifecycleEvents.assetId],
    references: [assets.id],
  }),
  performer: one(users, {
    fields: [assetLifecycleEvents.performedBy],
    references: [users.id],
  }),
}));

export const assetAlertsRelations = relations(assetAlerts, ({ one }) => ({
  organization: one(organizations, {
    fields: [assetAlerts.organizationId],
    references: [organizations.id],
  }),
  asset: one(assets, {
    fields: [assetAlerts.assetId],
    references: [assets.id],
  }),
  customer: one(customers, {
    fields: [assetAlerts.customerId],
    references: [customers.id],
  }),
  acknowledger: one(users, {
    fields: [assetAlerts.acknowledgedBy],
    references: [users.id],
  }),
  qbrRecommendation: one(qbrRecommendations, {
    fields: [assetAlerts.qbrRecommendationId],
    references: [qbrRecommendations.id],
  }),
  budgetLineItem: one(customerBudgetLineItems, {
    fields: [assetAlerts.budgetLineItemId],
    references: [customerBudgetLineItems.id],
  }),
}));

// Insert schemas for Asset Lifecycle tables
// Helper for coercing date strings to Date objects
const coerceDate = z.preprocess(
  (val) => {
    if (val === null || val === undefined || val === "") return undefined;
    if (val instanceof Date) return val;
    if (typeof val === "string") return new Date(val);
    return val;
  },
  z.date().optional()
);

// Helper for optional string fields - converts empty strings to undefined before XSS validation
const optionalXssSafeString = (maxLength: number) => z.preprocess(
  (val) => (val === "" || val === null || val === undefined) ? undefined : val,
  xssSafeString(maxLength).optional()
);

const optionalXssSafeTextarea = (maxLength: number) => z.preprocess(
  (val) => (val === "" || val === null || val === undefined) ? undefined : val,
  xssSafeTextarea(maxLength).optional()
);

export const insertAssetSchema = createInsertSchema(assets)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(255),
    assetTag: optionalXssSafeString(100),
    serialNumber: optionalXssSafeString(255),
    assetType: z.enum(ASSET_TYPES),
    category: optionalXssSafeString(100),
    manufacturer: optionalXssSafeString(255),
    model: optionalXssSafeString(255),
    status: z.enum(ASSET_STATUSES).optional(),
    lifecycleStage: z.enum(LIFECYCLE_STAGES).optional(),
    condition: z.enum(ASSET_CONDITIONS).optional(),
    location: optionalXssSafeString(255),
    assignedTo: optionalXssSafeString(255),
    department: optionalXssSafeString(100),
    notes: optionalXssSafeTextarea(2000),
    // Date fields - coerce strings to Date objects
    purchaseDate: coerceDate,
    warrantyStartDate: coerceDate,
    warrantyEndDate: coerceDate,
    endOfLifeDate: coerceDate,
    endOfSupportDate: coerceDate,
    expectedReplacementDate: coerceDate,
    lastSyncedAt: coerceDate,
    // Numeric fields - coerce to strings for decimal columns
    purchaseCost: z.preprocess(
      (val) => {
        if (val === "" || val === null || val === undefined) return undefined;
        if (typeof val === "number") return val.toString();
        return val;
      },
      z.string().optional()
    ),
    replacementCost: z.preprocess(
      (val) => {
        if (val === "" || val === null || val === undefined) return undefined;
        if (typeof val === "number") return val.toString();
        return val;
      },
      z.string().optional()
    ),
    currentValue: z.preprocess(
      (val) => {
        if (val === "" || val === null || val === undefined) return undefined;
        if (typeof val === "number") return val.toString();
        return val;
      },
      z.string().optional()
    ),
  });

export const insertAssetWarrantySchema = createInsertSchema(assetWarranties)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    warrantyType: xssSafeString(100).optional(),
    provider: xssSafeString(255).optional(),
    contractNumber: xssSafeString(255).optional(),
    coverageDescription: xssSafeTextarea(1000).optional(),
  });

export const insertAssetLifecycleEventSchema = createInsertSchema(assetLifecycleEvents)
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    eventType: xssSafeString(50),
    description: xssSafeTextarea(1000).optional(),
    notes: xssSafeTextarea(1000).optional(),
  });

export const insertAssetAlertSchema = createInsertSchema(assetAlerts)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    alertType: xssSafeString(50),
    title: xssSafeString(255),
    message: xssSafeTextarea(2000),
    recommendedAction: xssSafeTextarea(1000).optional(),
  });

export const insertHardwareCatalogSchema = createInsertSchema(hardwareCatalog)
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    manufacturer: xssSafeString(255),
    model: xssSafeString(255),
    modelNumber: xssSafeString(255).optional(),
    assetType: z.enum(ASSET_TYPES),
    category: xssSafeString(100).optional(),
  });

export const insertAssetAuditSchema = createInsertSchema(assetAudits)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    status: z.enum(['OK', 'FAIL', 'WARNING']).default('OK'),
    auditType: xssSafeString(50).default('manual'),
    failureReason: xssSafeTextarea(2000).optional(),
    failureCategory: xssSafeString(100).optional(),
    severity: z.enum(['low', 'medium', 'high', 'critical']).optional(),
    remediationNotes: xssSafeTextarea(2000).optional(),
    resolutionNotes: xssSafeTextarea(2000).optional(),
  });

export const insertTechStackAuditRuleSchema = createInsertSchema(techStackAuditRules)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: xssSafeString(255),
    description: xssSafeTextarea(1000).optional(),
    category: xssSafeString(100),
    severity: z.enum(['low', 'medium', 'high', 'critical']).default('medium'),
  });

// Types for Asset Lifecycle tables
export type InsertAsset = z.infer<typeof insertAssetSchema>;
export type Asset = typeof assets.$inferSelect;
export type InsertAssetWarranty = z.infer<typeof insertAssetWarrantySchema>;
export type AssetWarranty = typeof assetWarranties.$inferSelect;
export type InsertAssetLifecycleEvent = z.infer<typeof insertAssetLifecycleEventSchema>;
export type AssetLifecycleEvent = typeof assetLifecycleEvents.$inferSelect;
export type InsertAssetAlert = z.infer<typeof insertAssetAlertSchema>;
export type AssetAlert = typeof assetAlerts.$inferSelect;
export type InsertHardwareCatalog = z.infer<typeof insertHardwareCatalogSchema>;
export type HardwareCatalog = typeof hardwareCatalog.$inferSelect;
export type InsertAssetAudit = z.infer<typeof insertAssetAuditSchema>;
export type AssetAudit = typeof assetAudits.$inferSelect;
export type InsertTechStackAuditRule = z.infer<typeof insertTechStackAuditRuleSchema>;
export type TechStackAuditRule = typeof techStackAuditRules.$inferSelect;

// Asset alert types for notifications
export const AssetAlertType = {
  WARRANTY_EXPIRING_90: 'warranty_expiring_90',
  WARRANTY_EXPIRING_60: 'warranty_expiring_60',
  WARRANTY_EXPIRING_30: 'warranty_expiring_30',
  WARRANTY_EXPIRED: 'warranty_expired',
  EOL_APPROACHING_180: 'eol_approaching_180',
  EOL_APPROACHING_90: 'eol_approaching_90',
  EOL_REACHED: 'eol_reached',
  EOS_APPROACHING: 'eos_approaching',
  EOS_REACHED: 'eos_reached',
  REPLACEMENT_DUE: 'replacement_due',
  AGING_ASSET: 'aging_asset',
} as const;

export type AssetAlertTypeValue = typeof AssetAlertType[keyof typeof AssetAlertType];

// Lifecycle event types
export const LifecycleEventType = {
  PURCHASED: 'purchased',
  DEPLOYED: 'deployed',
  WARRANTY_EXTENDED: 'warranty_extended',
  REPAIRED: 'repaired',
  UPGRADED: 'upgraded',
  RELOCATED: 'relocated',
  REASSIGNED: 'reassigned',
  RETIRED: 'retired',
  DISPOSED: 'disposed',
  STATUS_CHANGED: 'status_changed',
  IMPORTED: 'imported',
} as const;

export type LifecycleEventTypeValue = typeof LifecycleEventType[keyof typeof LifecycleEventType];


// ============================================================================
// MICROSOFT 365 SECURE SCORE & LICENSE UTILIZATION TABLES
// ============================================================================

// M365 customer tenant mapping - links TechAlign customers to their Azure AD tenant
export const m365CustomerMapping = pgTable(
  "m365_customer_mapping",
  {
    id: serial("id").primaryKey(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    integrationId: integer("integration_id")
      .references(() => integrations.id, { onDelete: "cascade" })
      .notNull(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    tenantId: varchar("tenant_id").notNull(), // Customer's Azure AD tenant ID
    tenantDomain: varchar("tenant_domain"), // e.g., "customer.onmicrosoft.com"
    tenantDisplayName: varchar("tenant_display_name"), // Friendly name from Graph
    lastSyncedAt: timestamp("last_synced_at"),
    syncStatus: varchar("sync_status").default("pending"), // pending, syncing, success, error
    lastError: text("last_error"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_m365_mapping_customer").on(table.customerId),
    index("idx_m365_mapping_integration").on(table.integrationId),
    index("idx_m365_mapping_organization").on(table.organizationId),
    index("idx_m365_mapping_tenant").on(table.tenantId),
  ],
);

// M365 Secure Score history - stores daily score snapshots for trend tracking
export const m365SecureScores = pgTable(
  "m365_secure_scores",
  {
    id: serial("id").primaryKey(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    integrationId: integer("integration_id")
      .references(() => integrations.id, { onDelete: "cascade" })
      .notNull(),
    currentScore: decimal("current_score", { precision: 8, scale: 2 }),
    maxScore: decimal("max_score", { precision: 8, scale: 2 }),
    scorePercentage: decimal("score_percentage", { precision: 5, scale: 2 }),
    // Per-control breakdown from the secureScores response
    controlScores: jsonb("control_scores"), // Array of { controlName, score, maxScore, description }
    // The date this score was recorded by Microsoft (from createdDateTime)
    scoreDate: timestamp("score_date"),
    syncedAt: timestamp("synced_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_m365_scores_customer").on(table.customerId),
    index("idx_m365_scores_organization").on(table.organizationId),
    index("idx_m365_scores_date").on(table.scoreDate),
    index("idx_m365_scores_org_customer").on(table.organizationId, table.customerId),
  ],
);

// M365 Secure Score Recommendations (control profiles)
export const m365Recommendations = pgTable(
  "m365_recommendations",
  {
    id: serial("id").primaryKey(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    integrationId: integer("integration_id")
      .references(() => integrations.id, { onDelete: "cascade" })
      .notNull(),
    controlName: varchar("control_name").notNull(),
    controlCategory: varchar("control_category"),
    title: varchar("title"),
    description: text("description"),
    remediationDescription: text("remediation_description"),
    maxScore: decimal("max_score", { precision: 8, scale: 2 }),
    currentScore: decimal("current_score", { precision: 8, scale: 2 }),
    scorePercentage: decimal("score_percentage", { precision: 5, scale: 2 }),
    implementationStatus: varchar("implementation_status"), // Default, ThirdParty, Ignored, AlternativeMitigation, Planned
    actionUrl: text("action_url"),
    userImpact: varchar("user_impact"), // Low, Moderate, High
    implementationCost: varchar("implementation_cost"), // Low, Moderate, High
    threats: jsonb("threats"), // Array of threat types this control mitigates
    tier: varchar("tier"), // Tier1, Tier2, Tier3
    deprecated: boolean("deprecated").default(false),
    syncedAt: timestamp("synced_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_m365_recs_customer").on(table.customerId),
    index("idx_m365_recs_organization").on(table.organizationId),
    index("idx_m365_recs_control").on(table.controlName),
    index("idx_m365_recs_status").on(table.implementationStatus),
    index("idx_m365_recs_org_customer").on(table.organizationId, table.customerId),
  ],
);

// M365 License Utilization - tracks SKU usage per customer tenant
export const m365LicenseUtilization = pgTable(
  "m365_license_utilization",
  {
    id: serial("id").primaryKey(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    integrationId: integer("integration_id")
      .references(() => integrations.id, { onDelete: "cascade" })
      .notNull(),
    skuId: varchar("sku_id").notNull(), // Microsoft SKU GUID
    skuPartNumber: varchar("sku_part_number").notNull(), // e.g., "SPE_E3", "AAD_PREMIUM"
    displayName: varchar("display_name"), // Friendly name from lookup table
    totalLicenses: integer("total_licenses").default(0), // prepaidUnits.enabled
    assignedLicenses: integer("assigned_licenses").default(0), // consumedUnits
    unassignedLicenses: integer("unassigned_licenses").default(0), // calculated
    warningUnits: integer("warning_units").default(0), // prepaidUnits.warning
    suspendedUnits: integer("suspended_units").default(0), // prepaidUnits.suspended
    capabilityStatus: varchar("capability_status"), // Enabled, Warning, Suspended, Deleted, LockedOut
    appliesTo: varchar("applies_to"), // User, Company
    servicePlans: jsonb("service_plans"), // Array of service plan details
    syncedAt: timestamp("synced_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_m365_licenses_customer").on(table.customerId),
    index("idx_m365_licenses_organization").on(table.organizationId),
    index("idx_m365_licenses_sku").on(table.skuId),
    index("idx_m365_licenses_org_customer").on(table.organizationId, table.customerId),
  ],
);

// M365 Sync Logs - tracks sync operations for M365 data
export const m365SyncLogs = pgTable(
  "m365_sync_logs",
  {
    id: serial("id").primaryKey(),
    integrationId: integer("integration_id")
      .references(() => integrations.id, { onDelete: "cascade" })
      .notNull(),
    organizationId: uuid("organization_id")
      .references(() => organizations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" }),
    syncType: varchar("sync_type").notNull(), // secure_score, recommendations, licenses, full
    status: varchar("status").notNull(), // success, error, partial
    recordsProcessed: integer("records_processed").default(0),
    recordsSuccessful: integer("records_successful").default(0),
    recordsFailed: integer("records_failed").default(0),
    errorDetails: jsonb("error_details"),
    metadata: jsonb("metadata"),
    startedAt: timestamp("started_at").defaultNow(),
    completedAt: timestamp("completed_at"),
  },
  (table) => [
    index("idx_m365_sync_integration").on(table.integrationId),
    index("idx_m365_sync_organization").on(table.organizationId),
    index("idx_m365_sync_type").on(table.syncType),
    index("idx_m365_sync_status").on(table.status),
  ],
);

// Relations for M365 tables
export const m365CustomerMappingRelations = relations(m365CustomerMapping, ({ one }) => ({
  customer: one(customers, {
    fields: [m365CustomerMapping.customerId],
    references: [customers.id],
  }),
  integration: one(integrations, {
    fields: [m365CustomerMapping.integrationId],
    references: [integrations.id],
  }),
  organization: one(organizations, {
    fields: [m365CustomerMapping.organizationId],
    references: [organizations.id],
  }),
}));

export const m365SecureScoresRelations = relations(m365SecureScores, ({ one }) => ({
  customer: one(customers, {
    fields: [m365SecureScores.customerId],
    references: [customers.id],
  }),
  organization: one(organizations, {
    fields: [m365SecureScores.organizationId],
    references: [organizations.id],
  }),
  integration: one(integrations, {
    fields: [m365SecureScores.integrationId],
    references: [integrations.id],
  }),
}));

export const m365RecommendationsRelations = relations(m365Recommendations, ({ one }) => ({
  customer: one(customers, {
    fields: [m365Recommendations.customerId],
    references: [customers.id],
  }),
  organization: one(organizations, {
    fields: [m365Recommendations.organizationId],
    references: [organizations.id],
  }),
  integration: one(integrations, {
    fields: [m365Recommendations.integrationId],
    references: [integrations.id],
  }),
}));

export const m365LicenseUtilizationRelations = relations(m365LicenseUtilization, ({ one }) => ({
  customer: one(customers, {
    fields: [m365LicenseUtilization.customerId],
    references: [customers.id],
  }),
  organization: one(organizations, {
    fields: [m365LicenseUtilization.organizationId],
    references: [organizations.id],
  }),
  integration: one(integrations, {
    fields: [m365LicenseUtilization.integrationId],
    references: [integrations.id],
  }),
}));

// Insert schemas for M365 tables
export const insertM365CustomerMappingSchema = createInsertSchema(m365CustomerMapping).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertM365SecureScoreSchema = createInsertSchema(m365SecureScores).omit({
  id: true,
  createdAt: true,
});

export const insertM365RecommendationSchema = createInsertSchema(m365Recommendations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertM365LicenseUtilizationSchema = createInsertSchema(m365LicenseUtilization).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertM365SyncLogSchema = createInsertSchema(m365SyncLogs).omit({
  id: true,
});

// Types for M365 tables
export type InsertM365CustomerMapping = z.infer<typeof insertM365CustomerMappingSchema>;
export type M365CustomerMapping = typeof m365CustomerMapping.$inferSelect;
export type InsertM365SecureScore = z.infer<typeof insertM365SecureScoreSchema>;
export type M365SecureScore = typeof m365SecureScores.$inferSelect;
export type InsertM365Recommendation = z.infer<typeof insertM365RecommendationSchema>;
export type M365Recommendation = typeof m365Recommendations.$inferSelect;
export type InsertM365LicenseUtilization = z.infer<typeof insertM365LicenseUtilizationSchema>;
export type M365LicenseUtilization = typeof m365LicenseUtilization.$inferSelect;
export type InsertM365SyncLog = z.infer<typeof insertM365SyncLogSchema>;
export type M365SyncLog = typeof m365SyncLogs.$inferSelect;

// ============================================================================
// COVE DATA PROTECTION INTEGRATION TABLES
// ============================================================================

// Cove customer mapping - links TechAlign customers to Cove partners
export const coveCustomerMapping = pgTable(
  "cove_customer_mapping",
  {
    id: serial("id").primaryKey(),
    organizationId: varchar("organization_id", { length: 100 }).notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    integrationId: integer("integration_id")
      .references(() => integrations.id, { onDelete: "cascade" })
      .notNull(),
    covePartnerId: varchar("cove_partner_id", { length: 100 }).notNull(),
    covePartnerName: varchar("cove_partner_name", { length: 255 }),
    covePartnerData: jsonb("cove_partner_data"),
    isAutoMatched: boolean("is_auto_matched").default(false),
    matchConfidence: decimal("match_confidence", { precision: 5, scale: 2 }),
    lastSyncedAt: timestamp("last_synced_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("idx_cove_mapping_org").on(table.organizationId),
    index("idx_cove_mapping_customer").on(table.customerId),
    index("idx_cove_mapping_integration").on(table.integrationId),
    index("idx_cove_mapping_partner").on(table.covePartnerId),
  ]
);

// Cove backup statistics cache - stores fetched backup stats per device
export const coveBackupStats = pgTable(
  "cove_backup_stats",
  {
    id: serial("id").primaryKey(),
    organizationId: varchar("organization_id", { length: 100 }).notNull(),
    integrationId: integer("integration_id")
      .references(() => integrations.id, { onDelete: "cascade" })
      .notNull(),
    customerId: integer("customer_id")
      .references(() => customers.id, { onDelete: "cascade" })
      .notNull(),
    covePartnerId: varchar("cove_partner_id", { length: 100 }).notNull(),
    coveAccountId: varchar("cove_account_id", { length: 100 }).notNull(),
    deviceName: varchar("device_name", { length: 255 }),
    computerName: varchar("computer_name", { length: 255 }), // Actual hostname (I18) for workstations
    deviceOs: varchar("device_os", { length: 100 }),
    deviceType: varchar("device_type", { length: 20 }), // 'workstation', 'server', 'm365', 'unknown'
    // Backup status fields
    lastSessionStatus: varchar("last_session_status", { length: 50 }), // completed, failed, in_progress, never
    lastCompletedStatus: varchar("last_completed_status", { length: 50 }), // status of last completed backup session
    lastSuccessfulBackup: timestamp("last_successful_backup"),
    lastBackupAttempt: timestamp("last_backup_attempt"),
    selectedSize: bigint("selected_size", { mode: "number" }), // bytes
    usedStorage: bigint("used_storage", { mode: "number" }), // bytes
    dataSourcesEnabled: jsonb("data_sources_enabled"), // e.g. ["Files", "SystemState", "Exchange"]
    backupErrors: integer("backup_errors").default(0),
    backupWarnings: integer("backup_warnings").default(0),
    m365Details: jsonb("m365_details"), // M365-specific: { domains, billableUsers, exchange, onedrive, sharepoint, teams }
    deviceData: jsonb("device_data"), // Full raw stats from Cove API
    lastSyncedAt: timestamp("last_synced_at").defaultNow(),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
    assetId: integer("asset_id").references(() => assets.id, { onDelete: "set null" }),
  },
  (table) => [
    index("idx_cove_stats_org").on(table.organizationId),
    index("idx_cove_stats_integration").on(table.integrationId),
    index("idx_cove_stats_customer").on(table.customerId),
    index("idx_cove_stats_partner").on(table.covePartnerId),
    index("idx_cove_stats_account").on(table.coveAccountId),
    index("idx_cove_stats_asset").on(table.assetId),
  ]
);

// Insert schemas for Cove tables
export const insertCoveCustomerMappingSchema = createInsertSchema(coveCustomerMapping).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCoveBackupStatsSchema = createInsertSchema(coveBackupStats).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types for Cove tables
export type InsertCoveCustomerMapping = z.infer<typeof insertCoveCustomerMappingSchema>;
export type CoveCustomerMapping = typeof coveCustomerMapping.$inferSelect;
export type InsertCoveBackupStats = z.infer<typeof insertCoveBackupStatsSchema>;
export type CoveBackupStats = typeof coveBackupStats.$inferSelect;

// Normalized backup device fields for vendor-agnostic display and mapping
export interface NormalizedBackupDevice {
  id: number;
  provider: string;           // e.g., "cove", "veeam", "datto"
  integrationId: number;
  customerId: number;
  organizationId: string;
  deviceName: string | null;
  hostname: string | null;
  deviceType: string | null;  // "server", "workstation", "m365", "unknown"
  backupStatus: string | null;          // last session status
  lastCompletedStatus: string | null;
  lastSuccessfulBackup: Date | null;
  selectedSize: number | null;          // bytes
  usedStorage: number | null;           // bytes
  assetId: number | null;
  assetName: string | null;             // joined from assets table
}

// Zod validation schemas for backup mapping API requests
export const setBackupMappingSchema = z.object({
  assetId: z.coerce.number().int().positive(),
});

export const backupMappingParamsSchema = z.object({
  deviceId: z.string().regex(/^\d+$/).transform(Number),
});

// ============================================================================
// Executive Report Builder — Widget & Layout Schemas
// ============================================================================

// Widget type enum — all available widget types in the Widget Catalog
export const widgetTypeEnum = z.enum([
  'kpi_card',
  'data_table',
  'bar_chart',
  'line_chart',
  'pie_chart',
  'narrative_text',
  'logo_image',
  'section_header',
  'alignment_gauge',
  'recommendation_list',
]);

// Data source type enum — all available data binding sources
export const dataSourceTypeEnum = z.enum([
  'assessment_scores',
  'budget_overview',
  'budget_variance',
  'vendor_spend_breakdown',
  'key_findings',
  'recommendations_status',
  'alignment_score_trend',
  'backup_status',
  'patch_status',
  'consolidation_opportunities',
]);

// Extended template type enum for executive report builder
export const reportTemplateTypeEnum = z.enum([
  'executive_summary',
  'qbr',
  'assessment_overview',
  'budget_analysis',
  'custom',
  // Legacy types retained for backward compatibility
  'variance_analysis',
  'vendor_analysis',
  'cost_optimization',
]);

// Widget style overrides — optional per-widget visual customization
export const widgetStyleOverridesSchema = z.object({
  colorScheme: z.string().optional(),
  backgroundColor: z.string().optional(),
  borderRadius: z.number().min(0).max(24).optional(),
  padding: z.number().min(0).max(48).optional(),
}).optional();

// Data binding configuration — connects a widget to a data source with optional filters
export const dataBindingConfigSchema = z.object({
  source: dataSourceTypeEnum,
  filters: z.object({
    customerId: z.number().int().positive().optional(),
    dateRange: z.object({
      startDate: z.string().datetime(),
      endDate: z.string().datetime(),
    }).optional(),
    assessmentId: z.number().int().positive().optional(),
  }).optional(),
});

// Widget definition — a single widget instance within a report layout
export const widgetDefinitionSchema = z.object({
  id: z.string().min(1),
  type: widgetTypeEnum,
  position: z.object({
    col: z.number().int().min(0).max(11),
    row: z.number().int().min(0),
  }),
  size: z.object({
    colSpan: z.number().int().min(1).max(12),
    rowSpan: z.number().int().min(1).max(12),
  }),
  dataBinding: dataBindingConfigSchema.optional(),
  style: widgetStyleOverridesSchema,
  title: z.string().max(255).optional(),
});

// Layout definition — the top-level structure stored in reportTemplates.content
export const layoutDefinitionSchema = z.object({
  version: z.literal(1),
  gridColumns: z.literal(12),
  widgets: z.array(widgetDefinitionSchema),
});

// Inferred TypeScript types from Zod schemas
export type WidgetType = z.infer<typeof widgetTypeEnum>;
export type DataSourceType = z.infer<typeof dataSourceTypeEnum>;
export type WidgetStyleOverrides = z.infer<typeof widgetStyleOverridesSchema>;
export type DataBindingConfig = z.infer<typeof dataBindingConfigSchema>;
export type WidgetDefinition = z.infer<typeof widgetDefinitionSchema>;
export type LayoutDefinition = z.infer<typeof layoutDefinitionSchema>;
export type ReportTemplateType = z.infer<typeof reportTemplateTypeEnum>;
